import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { useProjects } from '../hooks/useProjects';
import { useTickets } from '../hooks/useTickets';
import { t } from '../localization';
import parseLinks from '../parseLinks';
import { exportTicketsToExcel } from '../services/exportService';
import {
  Container,
  Box,
  IconButton,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  FormControl,
  Chip,
  Stack,
  Tabs,
  Tab,
  Grid,
  MenuItem,
  InputLabel,
  Select,
  Tooltip,
  TablePagination,
  Avatar,
  Autocomplete,
  Badge,
  CircularProgress,
  InputAdornment,
  Collapse,
  Link
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Close as CloseIcon,
  AttachFile as AttachFileIcon,
  SupportAgent as SupportAgentIcon,
  Undo as UndoIcon,
  Person as PersonIcon,
  Folder as FolderIcon,
  Event as EventIcon,
  AccessTime as AccessTimeIcon,
  Info as InfoIcon,
  Description as DescriptionIcon,
  Chat as ChatIcon,
  FileDownload as FileDownloadIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { ticketsApi, authApi } from '../services/api';
import { projectsApi } from '../services/api';
import TicketForm from '../components/TicketForm';
import TicketList from '../components/TicketList';
import TicketDetails from '../components/TicketDetails';
import StatusChip from './StatusChip';
import TypeChip, { TYPE_LABELS } from './TypeChip';
import PriorityChip from './PriorityChip';
import PriorityStripe from './PriorityStripe';
import { STATUS_COLORS } from './StatusChip';
import FileAttachment from '../components/FileAttachment';
import CustomFieldsFilter from '../components/CustomFieldsFilter';
import * as statusService from '../services/statusService';
import html2canvas from 'html2canvas';

// Функция для форматирования отдельных частей даты
const formatDatePart = (dateString, part = 'full') => {
  if (!dateString) return '-';
  
  const date = new Date(dateString);
  
  // Только дата (DD.MM.YYYY)
  if (part === 'date') {
    return date.toLocaleDateString('ru-RU', {day: '2-digit', month: '2-digit', year: 'numeric'});
  }
  
  // Только время (HH:MM)
  if (part === 'time') {
    return date.toLocaleTimeString('ru-RU', {hour: '2-digit', minute:'2-digit'});
  }
  
  // Полная дата и время
  return date.toLocaleDateString('ru-RU', {day: '2-digit', month: '2-digit', year: 'numeric'}) + ' ' + 
         date.toLocaleTimeString('ru-RU', {hour: '2-digit', minute:'2-digit'});
};

// Сохраняем старую функцию для обратной совместимости
const formatDate = (dateString, dateOnly = false) => {
  if (dateOnly) {
    return formatDatePart(dateString, 'date');
  }
  return formatDatePart(dateString, 'full');
};

const getTicketPlannedDateMs = (dateString) => {
  if (!dateString) {
    return null;
  }
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.getTime();
};

const getFilterDateMs = (value, endOfDay = false) => {
  if (!value) {
    return null;
  }
  const parts = value.split('-').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) {
    return null;
  }
  const [year, month, day] = parts;
  if (endOfDay) {
    return new Date(year, month - 1, day, 23, 59, 59, 999).getTime();
  }
  return new Date(year, month - 1, day).getTime();
};

const DAY_MS = 24 * 60 * 60 * 1000;
const LEGACY_FINAL_STATUSES = new Set(['resolved', 'closed', 'done', 'completed', 'cancelled', 'canceled']);

const formatInputDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isTicketInFinalStatus = (ticket) => {
  if (ticket?.statusObj) {
    if (typeof ticket.statusObj.isFinal === 'boolean') {
      return ticket.statusObj.isFinal;
    }

    const statusObjName = String(ticket.statusObj.name || '').toLowerCase();
    if (statusObjName && LEGACY_FINAL_STATUSES.has(statusObjName)) {
      return true;
    }
  }

  const statusString = String(ticket?.status || '').toLowerCase();
  return statusString ? LEGACY_FINAL_STATUSES.has(statusString) : false;
};

const TicketsPage = ({ user, logout }) => {
  const isAudioFile = (name = '') => {
    const audioExts = ['.mp3', '.m4a', '.aac', '.wav', '.ogg', '.oga', '.opus', '.flac', '.webm'];
    const lower = String(name || '').toLowerCase();
    const dotExt = '.' + lower.split('.').pop();
    return audioExts.includes(dotExt);
  };
  // --- ВСЕ ХУКИ В САМОМ НАЧАЛЕ ---
  const { projects: fetchedProjects, isLoading: projectsLoading } = useProjects(!!user && user.role === 'admin');
  const [projects, setProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [projectInputValue, setProjectInputValue] = useState('');
  
  // Создаем обертку для setProjects с отладкой
  const setProjectsWithDebug = (newProjects) => {
    console.log('setProjects called with:', newProjects);
    console.trace('setProjects called from');
    setProjects(newProjects);
  };
  
  // Отслеживаем изменения projects для отладки
  useEffect(() => {
    console.log('projects changed:', projects);
    console.trace('projects changed stack trace');
  }, [projects]);
  const [selectedProject, setSelectedProject] = useState(() => {
    // Восстанавливаем выбранный проект из localStorage
    const savedProject = localStorage.getItem('selectedProject');
    return savedProject || '';
  });
  const [searchText, setSearchText] = useState('');
  const [selectedAssignee, setSelectedAssignee] = useState(null);
  const [selectedAuthor, setSelectedAuthor] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedType, setSelectedType] = useState('');
  const [plannedDateFrom, setPlannedDateFrom] = useState('');
  const [plannedDateTo, setPlannedDateTo] = useState('');
  const [customFieldsFilters, setCustomFieldsFilters] = useState({});
  const [isCustomFieldsFilterOpen, setIsCustomFieldsFilterOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [projectUsers, setProjectUsers] = useState([]);
  const [projectAssignees, setProjectAssignees] = useState([]);
  const [projectAuthors, setProjectAuthors] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  // Состояния для редактирования комментария
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editedCommentText, setEditedCommentText] = useState('');
  const [attachmentsToDelete, setAttachmentsToDelete] = useState([]);
  const navigate = useNavigate();
  const { ticketWithPrefix } = useParams();
  const { 
    tickets = [], 
    isLoading, 
    error,
    createTicket,
    updateTicket,
    deleteTicket,
    addComment,
    refetch
  } = useTickets({
    enabled: user !== null
  });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [projectStatuses, setProjectStatuses] = useState({});
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState(null);
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const [newComment, setNewComment] = useState('');
  const [commentAttachments, setCommentAttachments] = useState([]);
  const [tab, setTab] = useState(0);
  const [statsReport, setStatsReport] = useState('statuses');
  const [plannedWithoutDateOnly, setPlannedWithoutDateOnly] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [exportStatsLoading, setExportStatsLoading] = useState(false);
  const statsChartRef = useRef(null);
  const [pendingStatusParam, setPendingStatusParam] = useState(null);

  const plannedDateFilter = useMemo(() => {
    const makeSingleDayRange = (value) => ({
      from: getFilterDateMs(value),
      to: getFilterDateMs(value, true)
    });

    if (plannedDateFrom && plannedDateTo) {
      const from = getFilterDateMs(plannedDateFrom);
      const to = getFilterDateMs(plannedDateTo, true);

      if (from !== null && to !== null && from > to) {
        return { from: to, to: from };
      }

      return { from, to };
    }

    if (plannedDateFrom) {
      return makeSingleDayRange(plannedDateFrom);
    }

    if (plannedDateTo) {
      return makeSingleDayRange(plannedDateTo);
    }

    return { from: null, to: null };
  }, [plannedDateFrom, plannedDateTo]);

  const legacyStatusLabels = { new: 'Новый', open: 'Открыто', in_progress: 'В работе', resolved: 'Решен', closed: 'Закрыто' };

  const getTicketProjectId = (ticket) => String(ticket?.projectId ?? ticket?.project?.id ?? '');

  const normalizeProjectId = (id) => (id === null || id === undefined ? '' : String(id));
  const projectParam = searchParams.get('project');
  const selectedProjectId = normalizeProjectId(projectParam !== null ? projectParam : selectedProject);
  const projectOptions = useMemo(() => {
    const normalizedProjects = Array.isArray(allProjects)
      ? allProjects.map(p => ({ ...p, id: normalizeProjectId(p.id) }))
      : [];
    return [{ id: '', name: 'Все' }, ...normalizedProjects];
  }, [allProjects]);

  const updateSearchParams = useCallback((applyFn) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      applyFn(params);
      return params;
    });
  }, [setSearchParams]);

  const filteredTickets = useMemo(() => tickets.filter(ticket => {

    const ticketProjectId = getTicketProjectId(ticket);

    if (selectedProjectId && ticketProjectId !== selectedProjectId) {

      return false;

    }



    if (selectedAssignee && (!ticket.assignedTo || ticket.assignedTo.id !== selectedAssignee.id)) {

      return false;

    }



    if (selectedAuthor && (!ticket.createdBy || ticket.createdBy.id !== selectedAuthor.id)) {

      return false;

    }



    if (selectedStatus) {

      if (ticket.statusObj && ticket.statusObj.name && selectedStatus && selectedStatus.name) {

        if (ticket.statusObj.name.toLowerCase() !== selectedStatus.name.toLowerCase()) {

          return false;

        }

      } else if (!ticket.statusObj) {

        if (ticket.status && typeof ticket.status === 'string') {

          const legacyLabel = legacyStatusLabels[ticket.status.toLowerCase()];

          if (legacyLabel && legacyLabel.toLowerCase() !== selectedStatus.name.toLowerCase()) {

            return false;

          }

        }

      }

    }



    if (searchText) {

      const searchLower = searchText.toLowerCase();

      const title = ticket.title ? ticket.title.toLowerCase() : '';

      const id = String(ticket.id);

      const ticketPrefix = ticket.project?.ticketPrefix || (projects?.find(p => p.id === ticket.projectId)?.ticketPrefix || '');

      const prefixedId = ticketPrefix ? `${ticketPrefix}-${id}` : id;

      const authorName = ticket.createdBy ? (ticket.createdBy.name ? ticket.createdBy.name.toLowerCase() : '') : '';



      if (!title.includes(searchLower) &&

          !prefixedId.toLowerCase().includes(searchLower) &&

          !id.includes(searchLower) &&

          !authorName.includes(searchLower)) {

        return false;

      }

    }



    if (plannedWithoutDateOnly) {

      if (ticket.plannedEndDate) {

        return false;

      }

    } else if (plannedDateFilter.from !== null || plannedDateFilter.to !== null) {

      const plannedDateMs = getTicketPlannedDateMs(ticket.plannedEndDate);



      if (plannedDateFilter.from !== null && (plannedDateMs === null || plannedDateMs < plannedDateFilter.from)) {

        return false;

      }



      if (plannedDateFilter.to !== null && (plannedDateMs === null || plannedDateMs > plannedDateFilter.to)) {

        return false;

      }

    }



    if (Object.keys(customFieldsFilters).length > 0) {

      if (!ticket.customFieldValues || ticket.customFieldValues.length === 0) {

        return false;

      }



      for (const [fieldName, filterValue] of Object.entries(customFieldsFilters)) {

        const fieldValue = ticket.customFieldValues.find(cf => cf.field.name === fieldName);



        if (!fieldValue) {

          return false;

        }



        const fieldType = fieldValue.field.type;

        const actualValue = fieldValue.value;



        if (filterValue === '' || filterValue === null || (Array.isArray(filterValue) && filterValue.length === 0)) {

          continue;

        }



        let matches = false;



        switch (fieldType) {

          case 'TEXT':

          case 'TEXTAREA':

            if (typeof actualValue === 'string' && typeof filterValue === 'string') {

              matches = actualValue.toLowerCase().includes(filterValue.toLowerCase());

            }

            break;

          case 'DATETIME':

            if (actualValue && filterValue) {

              const actualDate = new Date(actualValue);

              const filterDate = new Date(filterValue);

              matches = actualDate.getTime() === filterDate.getTime();

            }

            break;

          case 'MULTISELECT':

            if (Array.isArray(actualValue) && Array.isArray(filterValue)) {

              matches = filterValue.some(val => actualValue.includes(val));

            }

            break;

          case 'LIST':

            if (typeof actualValue === 'string' && typeof filterValue === 'string') {

              matches = actualValue === filterValue;

            }

            break;

          case 'CHECKBOX':

            if (typeof actualValue === 'boolean' && typeof filterValue === 'boolean') {

              matches = actualValue === filterValue;

            }

            break;

          default:

            break;

        }



        if (!matches) {

          return false;

        }

      }

    }



    if (selectedType && (ticket.type || 'unknown') !== selectedType) {

      return false;

    }



    return true;

  }), [

    tickets,

    selectedType,

    selectedProjectId,

    selectedAssignee,

    selectedAuthor,

    selectedStatus,

    searchText,

    plannedDateFilter,

    plannedWithoutDateOnly,

    customFieldsFilters,

    projects

  ]);



  const statsFilteredTickets = useMemo(() => {

    if (!selectedProjectId) {

      return [];

    }

    return filteredTickets.filter(ticket => getTicketProjectId(ticket) === selectedProjectId);

  }, [filteredTickets, selectedProjectId]);



  // Разбивка по типам заявок для вкладки «Статистика».
  // Форма ответа совпадает со statusStats, поэтому график рисуется тем же кодом.
  const TYPE_CHART_COLORS = { bug: '#d32f2f', feature: '#7b1fa2', unknown: '#9e9e9e' };
  const typeStats = useMemo(() => {
    if (!selectedProject || selectedProject === '') {
      return null;
    }
    const counts = new Map(Object.keys(TYPE_LABELS).map(key => [key, 0]));
    statsFilteredTickets.forEach(ticket => {
      const key = TYPE_LABELS[ticket.type] ? ticket.type : 'unknown';
      counts.set(key, counts.get(key) + 1);
    });
    const items = Array.from(counts.entries()).map(([key, count]) => ({
      id: key,
      name: TYPE_LABELS[key],
      color: TYPE_CHART_COLORS[key],
      count,
    }));
    return {
      total: statsFilteredTickets.length,
      items,
      max: items.reduce((acc, item) => Math.max(acc, item.count), 0),
      source: 'tickets',
    };
  }, [statsFilteredTickets, selectedProject]);

  const statusStats = useMemo(() => {

    if (!selectedProject || selectedProject === '') {

      return null;

    }



    const projectIdKey = selectedProjectId || '';

    const statuses = projectStatuses[projectIdKey] || [];

    const totalProjectTickets = tickets.filter(ticket => getTicketProjectId(ticket) === projectIdKey).length;

    const filteredProjectTickets = statsFilteredTickets;



    if (filteredProjectTickets.length === 0) {

      return {

        total: 0,

        items: [],

        source: statuses.length > 0 ? 'project' : 'tickets',

        max: 0,

        projectTotal: totalProjectTickets,

      };

    }



    if (statuses.length === 0) {

      const aggregate = new Map();

      filteredProjectTickets.forEach(ticket => {

        const name = ticket.statusObj?.name || ticket.status || 'Без статуса';

        const color = ticket.statusObj?.color || STATUS_COLORS[(ticket.status || '').toLowerCase()] || '#9e9e9e';

        const existing = aggregate.get(name) || { count: 0, color };

        aggregate.set(name, { count: existing.count + 1, color: existing.color || color });

      });



      const items = Array.from(aggregate.entries())

        .map(([name, data]) => ({

          id: name,

          name,

          count: data.count,

          color: data.color || '#9e9e9e',

          isFinal: false,

        }))

        .sort((a, b) => b.count - a.count);



      const max = items.reduce((acc, item) => Math.max(acc, item.count), 0);



      return {

        total: filteredProjectTickets.length,

        items,

        source: 'tickets',

        max,

        projectTotal: totalProjectTickets,

      };

    }



    const matchedTicketIds = new Set();

    const items = statuses.map(status => {

      const statusIdStr = String(status.id);

      const statusName = status.name || statusIdStr;

      let count = 0;



      filteredProjectTickets.forEach(ticket => {

        const byId = ticket.statusObj?.id !== undefined && String(ticket.statusObj.id) === statusIdStr;

        const byName = !byId && ticket.statusObj?.name && ticket.statusObj.name.toLowerCase() === statusName.toLowerCase();

        const byLegacy = !byId && !byName && typeof ticket.status === 'string' && ticket.status.toLowerCase() === statusName.toLowerCase();



        if (byId || byName || byLegacy) {

          count += 1;

          matchedTicketIds.add(ticket.id);

        }

      });



      return {

        id: statusIdStr,

        name: statusName,

        count,

        color: status.color || STATUS_COLORS[statusName.toLowerCase()] || '#9e9e9e',

        isFinal: !!status.isFinal,

      };

    });



    const unmatchedTickets = filteredProjectTickets.filter(ticket => !matchedTicketIds.has(ticket.id));

    if (unmatchedTickets.length > 0) {

      items.push({

        id: 'unmatched',

        name: 'Без статуса',

        count: unmatchedTickets.length,

        color: '#bdbdbd',

        isFinal: false,

      });

    }



    const max = items.reduce((acc, item) => Math.max(acc, item.count), 0);



    return {

      total: filteredProjectTickets.length,

      items,

      source: 'project',

      max,

      projectTotal: totalProjectTickets,

    };

  }, [projectStatuses, selectedProject, statsFilteredTickets, tickets]);



  const plannedStats = useMemo(() => {

    if (!selectedProject || selectedProject === '') {

      return null;

    }



    const projectIdKey = String(selectedProject);

    const filteredProjectTickets = statsFilteredTickets;

    const statuses = projectStatuses[projectIdKey] || [];

    const hasStatusMeta = statuses.length > 0;

    const overdueStatusIds = new Set();

    const overdueStatusNames = new Set();



    statuses.forEach(status => {

      if (status.countsInOverdue) {

        overdueStatusIds.add(String(status.id));

        if (status.name) {

          overdueStatusNames.add(status.name.toLowerCase());

        }

      }

    });



    const activeTickets = filteredProjectTickets.filter(ticket => !isTicketInFinalStatus(ticket));



    const eligibleTickets = activeTickets.filter(ticket => {

      if (isTicketInFinalStatus(ticket)) {

        return false;

      }



      const includeWhenMetaMissing = () => {

        if (ticket.statusObj && typeof ticket.statusObj.countsInOverdue === 'boolean') {

          return ticket.statusObj.countsInOverdue === true;

        }

        return !hasStatusMeta;

      };



      if (ticket.statusObj) {

        if (typeof ticket.statusObj.countsInOverdue === 'boolean') {

          return ticket.statusObj.countsInOverdue === true;

        }



        if (ticket.statusObj.id !== undefined && ticket.statusObj.id !== null) {

          if (overdueStatusIds.size === 0) {

            return false;

          }

          return overdueStatusIds.has(String(ticket.statusObj.id));

        }



        if (ticket.statusObj.name) {

          if (overdueStatusNames.size === 0) {

            return false;

          }

          return overdueStatusNames.has(ticket.statusObj.name.toLowerCase());

        }



        return includeWhenMetaMissing();

      }



      if (typeof ticket.status === 'string' && ticket.status) {

        if (overdueStatusNames.size === 0) {

          return includeWhenMetaMissing();

        }

        return overdueStatusNames.has(ticket.status.toLowerCase());

      }



      return includeWhenMetaMissing();

    });



    if (eligibleTickets.length === 0) {

      return {

        total: 0,

        scheduled: 0,

        withoutPlan: 0,

        items: [],

        max: 0,

      };

    }



    const aggregate = new Map();

    const today = new Date();

    today.setHours(0, 0, 0, 0);

    const todayMs = today.getTime();



    let scheduled = 0;

    let withoutPlan = 0;



    eligibleTickets.forEach(ticket => {

      const plannedMs = getTicketPlannedDateMs(ticket.plannedEndDate);



      if (plannedMs === null) {

        withoutPlan += 1;

        const existing = aggregate.get('no-date') || {

          id: 'no-date',

          name: 'Без плановой даты',

          count: 0,

          isOverdue: false,

          date: null,

          relativeLabel: '',

          color: '#bdbdbd',

        };

        aggregate.set('no-date', { ...existing, count: existing.count + 1 });

        return;

      }



      scheduled += 1;



      const normalizedDate = new Date(plannedMs);

      normalizedDate.setHours(0, 0, 0, 0);

      const normalizedMs = normalizedDate.getTime();



      const diffDays = Math.round((normalizedMs - todayMs) / DAY_MS);

      const isOverdue = normalizedMs < todayMs;



      let relativeLabel = '';

      if (diffDays === 0) {

        relativeLabel = 'сегодня';

      } else if (diffDays === 1) {

        relativeLabel = 'завтра';

      } else if (diffDays === -1) {

        relativeLabel = 'вчера';

      } else if (diffDays > 1) {

        relativeLabel = `через ${diffDays} дн.`;

      } else if (diffDays < -1) {

        relativeLabel = `просрочено на ${Math.abs(diffDays)} дн.`;

      }



      const determineColor = () => {

        if (isOverdue) {

          return '#ef5350';

        }

        if (diffDays === 0) {

          return '#ffa726';

        }

        return '#42a5f5';

      };



      const existing = aggregate.get(normalizedMs);

      if (existing) {

        aggregate.set(normalizedMs, {

          ...existing,

          count: existing.count + 1,

          isOverdue: existing.isOverdue || isOverdue,

        });

      } else {

        aggregate.set(normalizedMs, {

          id: String(normalizedMs),

          name: formatDatePart(normalizedDate.toISOString(), 'date'),

          count: 1,

          isOverdue,

          date: normalizedDate,

          relativeLabel,

          color: determineColor(),

        });

      }

    });



    const items = Array.from(aggregate.values()).sort((a, b) => {

      if (a.date === null && b.date === null) {

        return 0;

      }

      if (a.date === null) {

        return 1;

      }

      if (b.date === null) {

        return -1;

      }

      if (a.isOverdue !== b.isOverdue) {

        return a.isOverdue ? -1 : 1;

      }

      return a.date.getTime() - b.date.getTime();

    });



    const max = items.reduce((acc, item) => Math.max(acc, item.count), 0);



    return {

      total: eligibleTickets.length,

      scheduled,

      withoutPlan,

      items,

      max,

    };

  }, [projectStatuses, selectedProject, statsFilteredTickets]);



  const handleExportStatsPng = async () => {
    const chart = statsReport === 'types' ? typeStats : statusStats;
    if (!statsChartRef.current || !chart || chart.total === 0) {
      return;
    }

    try {
      setExportStatsLoading(true);
      const canvas = await html2canvas(statsChartRef.current, {
        backgroundColor: '#ffffff',
        scale: window.devicePixelRatio || 1,
      });

      const projectName = projects.find(p => String(p.id) === String(selectedProject))?.name || 'project';
      const timestamp = new Date().toISOString().slice(0, 10);

      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      const reportSlug = statsReport === 'types' ? 'types' : 'status';
      link.download = `tickets-${reportSlug}-${projectName}-${timestamp}.png`;
      link.click();
    } catch (error) {
      console.error('Ошибка экспорта статистики в PNG:', error);
      alert('Не удалось экспортировать статистику в PNG. Попробуйте ещё раз.');
    } finally {
      setExportStatsLoading(false);
    }
  };

  useEffect(() => {
    if (!pendingStatusParam) {
      return;
    }

    if (!selectedProject || selectedProject === '') {
      return;
    }

    const projectIdKey = String(selectedProject);
    const statuses = projectStatuses[projectIdKey];
    if (!statuses || statuses.length === 0) {
      return;
    }

    if (pendingStatusParam === 'unmatched') {
      setSelectedStatus({ id: 'unmatched', name: 'Без статуса', color: '#bdbdbd' });
      setPendingStatusParam(null);
      return;
    }

    const matchById = statuses.find(status => String(status.id) === pendingStatusParam);
    const matchByName = statuses.find(status => (status.name || '').toLowerCase() === pendingStatusParam.toLowerCase());
    const targetStatus = matchById || matchByName;

    if (targetStatus) {
      setSelectedStatus(targetStatus);
      setPendingStatusParam(null);
    }
  }, [pendingStatusParam, selectedProject, projectStatuses]);

  const handleTypeLinkClick = (event, item) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedType(item.id);
    setTab(0);
    setPage(0);
  };

  const handleStatusLinkClick = (event, item) => {
    event.preventDefault();
    event.stopPropagation();

    if (!selectedProject || selectedProject === '') {
      return;
    }

    const projectIdKey = String(selectedProject);
    const statuses = projectStatuses[projectIdKey] || [];
    const targetStatus =
      statuses.find(status => String(status.id) === String(item.id)) ||
      statuses.find(status => (status.name || '').toLowerCase() === (item.name || '').toLowerCase());

    const statusValue = targetStatus || { id: item.id, name: item.name, color: item.color };
    setSelectedStatus(statusValue);

    const nextTab = item.isFinal ? 1 : 0;
    setTab(nextTab);
    const params = new URLSearchParams(searchParams);
    params.set('project', projectIdKey);
    const statusParamValue = statusValue.id !== undefined && statusValue.id !== null && statusValue.id !== ''
      ? String(statusValue.id)
      : (statusValue.name || item.id || item.name || '');
    if (statusParamValue) {
      params.set('status', statusParamValue);
    } else {
      params.delete('status');
    }

    if (nextTab === 1) {
      params.set('tab', 'archive');
    } else {
      params.delete('tab');
    }

    setSearchParams(params);
  };

  const handlePlannedLinkClick = (event, item) => {
    event.preventDefault();
    event.stopPropagation();

    const params = new URLSearchParams(searchParams);

    if (!selectedProject || selectedProject === '') {
      params.delete('plannedFrom');
      params.delete('plannedTo');
      params.delete('planned');
      params.delete('tab');
      setSearchParams(params);
      setPlannedWithoutDateOnly(item.id === 'no-date');
      if (item.id === 'no-date') {
        setPlannedDateFrom('');
        setPlannedDateTo('');
      }
      return;
    }

    if (selectedProject && selectedProject !== '') {
      params.set('project', String(selectedProject));
    }

    if (item.id === 'no-date') {
      setPlannedDateFrom('');
      setPlannedDateTo('');
      setPlannedWithoutDateOnly(true);
      params.set('planned', 'no-date');
      params.delete('plannedFrom');
      params.delete('plannedTo');
    } else if (item.date instanceof Date) {
      const filterDate = formatInputDate(item.date);
      setPlannedWithoutDateOnly(false);
      setPlannedDateFrom(filterDate);
      setPlannedDateTo(filterDate);
      params.set('plannedFrom', filterDate);
      params.set('plannedTo', filterDate);
      params.delete('planned');
    }

    params.delete('status');
    params.delete('tab');

    setSearchParams(params);
    setTab(0);
    setStatsReport('planned');
    setPage(0);
  };

  // Загрузка проектов и их статусов
  useEffect(() => {
    console.log('useEffect projects called with:', { fetchedProjects, userProjects: user?.projects, projectsLoading });
    console.log('Current projects state:', projects);
    
    // Определяем, какие проекты использовать
    let projectsToUse = [];
    
    // Приоритет: fetchedProjects (для админов)
    if (fetchedProjects?.length > 0) {
      projectsToUse = fetchedProjects;
    }
    
    // Если у нас уже есть проекты в состоянии, не очищаем их, если новые проекты пустые
    if (projectsToUse.length === 0 && projects.length > 0) {
      projectsToUse = projects;
    }
    
    console.log('projectsToUse:', projectsToUse);
    
    // Устанавливаем проекты
    if (projectsToUse.length > 0) {
      setProjectsWithDebug(projectsToUse);
      setAllProjects(projectsToUse);
      
      // Загружаем статусы для всех проектов
      projectsToUse.forEach(project => {
        loadProjectStatuses(project.id);
      });
      
      // Проверяем, есть ли сохраненный проект в localStorage
      const savedProject = localStorage.getItem('selectedProject');
      console.log('savedProject:', savedProject);
      
      if (savedProject === null || savedProject === undefined) {
        // Если нет сохраненного проекта, выбираем первый
        const firstProjectId = projectsToUse[0].id;
        setSelectedProject(firstProjectId);
        localStorage.setItem('selectedProject', firstProjectId);
        // Загружаем статусы первого проекта
        loadProjectStatuses(firstProjectId);
      } else if (savedProject === '') {
        // Если выбрано "Все", устанавливаем пустую строку
        setSelectedProject('');
      } else {
        // Проверяем, существует ли сохраненный проект в списке доступных
        const projectExists = projectsToUse.some(p => String(p.id) === String(savedProject));
        if (projectExists) {
          setSelectedProject(savedProject);
          loadProjectStatuses(savedProject);
        } else {
          // Если сохраненный проект недоступен, выбираем первый
          const firstProjectId = projectsToUse[0].id;
          setSelectedProject(firstProjectId);
          localStorage.setItem('selectedProject', firstProjectId);
          loadProjectStatuses(firstProjectId);
        }
      }
    }
  }, [fetchedProjects, user, projectsLoading]);
  
  // Автоматическое открытие диалога с деталями тикета при переходе по прямой ссылке
  useEffect(() => {
    if (ticketWithPrefix && tickets.length > 0) {
      // Извлекаем ID из формата PREFIX-ID или используем весь параметр, если формат не соответствует
      const idMatch = ticketWithPrefix.match(/(?:.*-)?([0-9]+)$/);
      const numericId = idMatch ? parseInt(idMatch[1], 10) : parseInt(ticketWithPrefix, 10);
      
      const ticketToOpen = tickets.find(t => t.id === numericId);
      if (ticketToOpen) {
        openDetailDialog(ticketToOpen);
      }
    }
  }, [ticketWithPrefix, tickets]);

  // Автоматический выбор проекта
  useEffect(() => {
    if (!projectsLoading && projects.length === 1) {
      const savedProject = localStorage.getItem('selectedProject');
      if (savedProject === null || savedProject === undefined) {
        handleProjectChange(projects[0].id);
      }
    }
  }, [projectsLoading, projects]);

  // Функция для загрузки статусов проекта
  const loadProjectStatuses = useCallback(async (projectId) => {
    try {
      const statuses = await statusService.getProjectStatuses(projectId);
      setProjectStatuses(prev => ({
        ...prev,
        [projectId]: statuses
      }));
    } catch (error) {
      console.error(`Ошибка при загрузке статусов для проекта ${projectId}:`, error);
    }
  }, []);

  // Обработчик изменения фильтров по кастомным полям
  const handleCustomFieldsFiltersChange = (newFilters) => {
    setCustomFieldsFilters(newFilters);
  };

  // Очистка фильтров по кастомным полям
  const handleClearCustomFieldsFilters = () => {
    setCustomFieldsFilters({});
  };

  // Сброс всех фильтров
  const handleClearAllFilters = () => {
    setSearchText('');
    setSelectedAssignee(null);
    setSelectedAuthor(null);
    setSelectedStatus(null);
    setSelectedType('');
    setPlannedDateFrom('');
    setPlannedDateTo('');
    setPlannedWithoutDateOnly(false);
    setCustomFieldsFilters({});
    setPage(0); // Сбрасываем пагинацию

    const params = new URLSearchParams(searchParams);
    params.delete('status');
    params.delete('plannedFrom');
    params.delete('plannedTo');
    params.delete('planned');
    params.delete('tab');
    setSearchParams(params);
  };

  // Проверяем, есть ли активные фильтры
  const hasActiveFilters = searchText || selectedAssignee || selectedAuthor || selectedStatus || plannedDateFrom || plannedDateTo || plannedWithoutDateOnly || Object.keys(customFieldsFilters).length > 0;

  // Функция для загрузки пользователей из тикетов
  const loadUsersFromTickets = () => {
    try {
      if (!tickets || tickets.length === 0) {
        // Нет загруженных тикетов для извлечения пользователей
        return;
      }
      
      // Создаем коллекции для уникальных пользователей
      const uniqueUsers = new Map();
      
      // Извлекаем пользователей из тикетов
      tickets.forEach(ticket => {
        // Добавляем автора тикета
        if (ticket.createdBy && ticket.createdBy.id) {
          uniqueUsers.set(ticket.createdBy.id, ticket.createdBy);
        }
        
        // Добавляем исполнителя тикета
        if (ticket.assignedTo && ticket.assignedTo.id) {
          uniqueUsers.set(ticket.assignedTo.id, ticket.assignedTo);
        }
      });
      
      // Преобразуем Map в массив пользователей
      const allUsers = Array.from(uniqueUsers.values());

      
      // Фильтруем агентов (исполнителей)
      const agents = allUsers.filter(user => user.role === 'agent');
      
      // Устанавливаем пользователей
      setUsers(allUsers);
      setProjectUsers(allUsers); // Все пользователи
      setProjectAssignees(agents); // Только агенты могут быть исполнителями
      setProjectAuthors(allUsers); // Все пользователи могут быть авторами
      
      // Если есть выбранный проект, фильтруем пользователей
      if (selectedProject !== undefined && selectedProject !== null) {
        filterProjectUsers(selectedProject, allUsers);
      }
    } catch (error) {
      console.error('Ошибка при извлечении пользователей из тикетов:', error);
    }
  };

  // Функция для фильтрации пользователей по проекту
  const filterProjectUsers = useCallback((projectId, sourceUsers = users) => {
    const normalize = (u) => {
      if (u && u.user) {
        // Ответ projectsApi.getProjectUsers может быть в виде { user, role }
        return { ...u.user, role: u.role || u.user.role };
      }
      return u;
    };

    const safeUsers = (Array.isArray(sourceUsers) ? sourceUsers : []).map(normalize).filter(Boolean);

    // Для режима "Все проекты" показываем объединение по всем пользователям
    if (!projectId) {
      const agents = safeUsers.filter(user => user.role === 'agent');
      setProjectUsers(safeUsers);
      setProjectAssignees(agents);
      setProjectAuthors(safeUsers);
      return;
    }

    if (safeUsers.length === 0) {
      setProjectUsers([]);
      setProjectAssignees([]);
      setProjectAuthors([]);
      return;
    }

    // Пытаемся отфильтровать пользователей, привязанных к конкретному проекту
    const projectUsersOnly = safeUsers.filter(user => {
      if (Array.isArray(user.projects) && user.projects.length > 0) {
        return user.projects.some(p => String(p.id) === String(projectId));
      }
      if (user.projectId !== undefined && user.projectId !== null) {
        return String(user.projectId) === String(projectId);
      }
      // Если нет связки с проектами, не исключаем пользователя (старые данные)
      return true;
    });

    const agents = projectUsersOnly.filter(user => user.role === 'agent');
    setProjectUsers(projectUsersOnly);
    setProjectAssignees(agents);
    setProjectAuthors(projectUsersOnly);
  }, [users]);

  // Загружаем пользователей выбранного проекта
  useEffect(() => {
    const activeProjectId = selectedProject || '';
    if (!activeProjectId) {
      // Для режима "Все проекты" используем пользователей из загруженных тикетов
      loadUsersFromTickets();
      return;
    }

    let cancelled = false;
    const fetchProjectUsers = async () => {
      try {
        const res = await projectsApi.getProjectUsers(activeProjectId);
        if (cancelled) return;
        const projectUserList = res.data || [];
        setUsers(projectUserList);
        filterProjectUsers(activeProjectId, projectUserList);
      } catch (error) {
        console.error('Ошибка при загрузке пользователей проекта:', error);
      }
    };

    fetchProjectUsers();
    return () => { cancelled = true; };
  }, [selectedProject, filterProjectUsers]);

  // Обработчик смены проекта
  const handleProjectChange = useCallback((projectId) => {
    const normalized = normalizeProjectId(projectId);

    setSelectedProject(normalized);
    localStorage.setItem('selectedProject', normalized);

    updateSearchParams((params) => {
      params.set('project', normalized || '');
      if (!normalized) {
        params.delete('status');
        params.delete('tab');
        params.delete('plannedFrom');
        params.delete('plannedTo');
        params.delete('planned');
      }
    });

    setSelectedAssignee(null);
    setSelectedAuthor(null);
    setSelectedStatus(null);
    setPage(0);

    filterProjectUsers(normalized);

    if (normalized) {
      loadProjectStatuses(normalized);
    }
  }, [filterProjectUsers, loadProjectStatuses, updateSearchParams]);

  // Следим за изменением query-параметров (tab/project/status)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const projectParam = searchParams.get('project');
    const statusParam = searchParams.get('status');
    const plannedFromParam = searchParams.get('plannedFrom');
    const plannedToParam = searchParams.get('plannedTo');
    const plannedNoDateParam = searchParams.get('planned');

    if (tabParam === 'archive') {
      setTab(1);
    } else if (tabParam === 'stats') {
      setTab(2);
    } else {
      setTab(0);
    }

    // Синхронизация выбранного проекта из query-параметра без перезаписи обратно в URL
    if (projectParam !== null && normalizeProjectId(selectedProject) !== normalizeProjectId(projectParam)) {
      const normalized = normalizeProjectId(projectParam);
      setSelectedProject(normalized);
      localStorage.setItem('selectedProject', normalized);
      filterProjectUsers(normalized);
      if (normalized) {
        loadProjectStatuses(normalized);
      }
    }

    if (statusParam) {
      setPendingStatusParam(statusParam);
    } else if (pendingStatusParam !== null) {
      setPendingStatusParam(null);
    }

    if (plannedNoDateParam === 'no-date') {
      if (!plannedWithoutDateOnly) {
        setPlannedWithoutDateOnly(true);
      }
      if (plannedDateFrom !== '') {
        setPlannedDateFrom('');
      }
      if (plannedDateTo !== '') {
        setPlannedDateTo('');
      }
    } else {
      if (plannedWithoutDateOnly) {
        setPlannedWithoutDateOnly(false);
      }

      if (plannedFromParam !== null && plannedFromParam !== plannedDateFrom) {
        setPlannedDateFrom(plannedFromParam);
      } else if (plannedFromParam === null && plannedDateFrom !== '') {
        setPlannedDateFrom('');
      }

      if (plannedToParam !== null && plannedToParam !== plannedDateTo) {
        setPlannedDateTo(plannedToParam);
      } else if (plannedToParam === null && plannedDateTo !== '') {
        setPlannedDateTo('');
      }
    }
  }, [
    searchParams,
    selectedProjectId,
    pendingStatusParam,
    handleProjectChange,
    filterProjectUsers,
    plannedWithoutDateOnly,
    plannedDateFrom,
    plannedDateTo
  ]);

  // Сброс страницы при смене фильтра
  useEffect(() => {
    setPage(0);
  }, [
    selectedProjectId,
    selectedStatus,
    selectedAssignee,
    selectedAuthor,
    searchText,
    plannedDateFrom,
    plannedDateTo,
    plannedWithoutDateOnly,
    customFieldsFilters
  ]);

  useEffect(() => {
    if ((plannedDateFrom || plannedDateTo) && plannedWithoutDateOnly) {
      setPlannedWithoutDateOnly(false);
    }
  }, [plannedDateFrom, plannedDateTo, plannedWithoutDateOnly]);

// --- ВСЕ ХУКИ ЗАКОНЧИЛИСЬ ---

  // --- Селектор проектов ---
  // Показываем всегда оба пункта: 'Все' и название проекта, даже если projects.length === 1


  // --- ДАЛЬШЕ ТОЛЬКО УСЛОВИЯ, ФУНКЦИИ, RETURN ---
  if (isLoading || projectsLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography variant="h5" align="center">
          {t('loading')}
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography variant="h5" align="center" color="error">
          {t('error')}
        </Typography>
      </Container>
    );
  }

  // --- ДАЛЬШЕ ТОЛЬКО ФУНКЦИИ/ОБРАБОТЧИКИ ---



  const handleTabChange = (event, value) => {
    setTab(value);
    if (value === 1) {
      // Вкладка "Архив" - тикеты в финальных статусах
      setSearchParams({ tab: 'archive' });
    } else if (value === 2) {
      // Вкладка статистики
      setSearchParams({ tab: 'stats' });
    } else {
      // Вкладка "Активные" - тикеты НЕ в финальных статусах
      setSearchParams({});
    }
  };



  // Старые захардкоженные метки статусов (только для обратной совместимости)
  const priorityLabels = { low: 'Низкий', normal: 'Средний', high: 'Высокий' };

  // Все хуки useEffect должны быть вызваны до любых условных операторов

  // Разрешение действий автора по статусу проекта
  const projectForSelectedTicket = selectedTicket
    ? projects.find(p => String(p.id) === String(selectedTicket.projectId || selectedTicket.project?.id))
    : null;
  const authorActionsFromStatusId = projectForSelectedTicket?.authorActionsFromStatusId;
  const canAuthorActFromCurrentStatus = !authorActionsFromStatusId
    ? true
    : !!(selectedTicket?.statusObj && String(selectedTicket.statusObj.id) === String(authorActionsFromStatusId));

  // Фильтрация тикетов

  const displayedTickets = filteredTickets.filter(t => {
    // Проверяем, находится ли тикет в финальном статусе
    const isInFinalStatus = t.statusObj?.isFinal === true;
    
    let byTab = true;
    if (tab === 1) {
      // Вкладка "Архив" - показываем только тикеты в финальных статусах
      byTab = isInFinalStatus;
    } else if (tab === 0) {
      // Вкладка "Активные" - показываем только тикеты НЕ в финальных статусах
      byTab = !isInFinalStatus;
    }
    return byTab;
  });

  const handleCreateTicket = async (data) => {
    try {
      // Если у пользователя только один проект - всегда используем его
      const finalProjectId = projects.length === 1 ? projects[0].id : data.projectId;
      
      let ticketData;
      // Проверяем, является ли data объектом FormData (создается при наличии вложений)
      if (data instanceof FormData) {
        // Если это FormData, убедимся, что title и другие обязательные поля присутствуют
        if (!data.get('title')) {
          console.error('Ошибка: отсутствует обязательное поле title');
          return;
        }
        
        // Убедимся, что projectId установлен
        if (finalProjectId) {
          data.set('projectId', finalProjectId);
        }
        
        ticketData = data;
      } else {
        // Обычный объект, проверим наличие обязательных полей
        if (!data.title) {
          console.error('Ошибка: отсутствует обязательное поле title');
          return;
        }
        
        ticketData = { 
          ...data,
          projectId: finalProjectId 
        };
      }
      
      // Отправка данных для создания тикета

      await createTicket(ticketData);
      await refetch(); // Явный рефетч после изменений
      setIsFormOpen(false);
    } catch (error) {
      console.error('Error creating ticket:', error);
    }
  };

  const handleEditTicket = async (ticket) => {
    // Открываем форму редактирования тикета
    // Проверка на финальный статус теперь происходит в интерфейсе путем скрытия кнопки
    setSelectedTicket(ticket);
    setIsFormOpen(true);
  };

  const handleUpdateTicket = async (data) => {
    try {
      // Всегда гарантируем, что projectId из формы есть в данных
      let ticketData = data;
      if (data.files && data.files.length > 0) {
        // Удаляем старый projectId, если есть, и ставим актуальный из формы
        data.set('projectId', data.get('projectId') || (selectedTicket && selectedTicket.projectId) || '');
        ticketData = data;
      } else {
        ticketData = { ...data, projectId: data.projectId };
      }
      await updateTicket({ id: selectedTicket.id, ticket: ticketData });
      setIsFormOpen(false);
      const res = await ticketsApi.getOne(selectedTicket.id);
      setSelectedTicket(res.data);
    } catch (e) {
      console.error('Update ticket error:', e.response?.data || e);
      alert('Ошибка обновления заявки: ' + JSON.stringify(e.response?.data || e.message));
    }
  };

  // Функция удаления тикета (без нативного диалога подтверждения)
  const handleDeleteTicket = (id) => {
    deleteTicket(id)
      .then(() => {
        // Обновляем список тикетов после удаления
        refetch();
      })
      .catch(error => {
        console.error('Ошибка при удалении тикета:', error);
      });
  };
  
  // Функция экспорта тикетов в Excel с учетом фильтров
  const handleExportToExcel = () => {
    setExportLoading(true);
    
    try {
      // Получаем отфильтрованные тикеты
      const filteredTickets = tickets.filter(ticket => {
        const ticketProjectId = getTicketProjectId(ticket);

        // Фильтрация по проекту
        if (selectedProject && selectedProject !== '' && ticketProjectId !== String(selectedProject)) {
          return false;
        }
        
        // Фильтрация по типу заявки
        if (selectedType && (ticket.type || 'unknown') !== selectedType) {
          return false;
        }

        // Фильтрация по исполнителю
        if (selectedAssignee && (!ticket.assignedTo || ticket.assignedTo.id !== selectedAssignee.id)) {
          return false;
        }
        
        // Фильтрация по автору
        if (selectedAuthor && (!ticket.createdBy || ticket.createdBy.id !== selectedAuthor.id)) {
          return false;
        }
        
        // Фильтрация по статусу
        if (selectedStatus) {
          // Для новой системы статусов (объекты)
          if (ticket.statusObj && selectedStatus.id) {
            if (ticket.statusObj.id !== selectedStatus.id) {
              return false;
            }
          }
          // Для старой системы статусов (строки)
          else if (typeof selectedStatus === 'string' || selectedStatus.name) {
            const statusName = typeof selectedStatus === 'string' ? selectedStatus : selectedStatus.name;
            const ticketStatusName = ticket.statusObj ? ticket.statusObj.name : ticket.status;
            if (ticketStatusName !== statusName) {
              return false;
            }
          } else {
            return false;
          }
        }

        // Фильтрация по плановой дате решения
        if (plannedWithoutDateOnly) {
          if (ticket.plannedEndDate) {
            return false;
          }
        } else if (plannedDateFilter.from !== null || plannedDateFilter.to !== null) {
          const plannedDateMs = getTicketPlannedDateMs(ticket.plannedEndDate);

          if (plannedDateFilter.from !== null && (plannedDateMs === null || plannedDateMs < plannedDateFilter.from)) {
            return false;
          }

          if (plannedDateFilter.to !== null && (plannedDateMs === null || plannedDateMs > plannedDateFilter.to)) {
            return false;
          }
        }

        // Фильтрация по кастомным полям
        if (Object.keys(customFieldsFilters).length > 0) {
          // Проверяем, есть ли у тикета кастомные поля
          if (!ticket.customFieldValues || ticket.customFieldValues.length === 0) {
            // Если у тикета нет кастомных полей, но есть активные фильтры - исключаем тикет
            return false;
          }

          // Проверяем каждый активный фильтр
          for (const [fieldName, filterValue] of Object.entries(customFieldsFilters)) {
            const fieldValue = ticket.customFieldValues.find(cf => cf.field.name === fieldName);
            
            if (!fieldValue) {
              // Если поле не найдено - исключаем тикет
              return false;
            }

            // Применяем фильтр в зависимости от типа поля
            const fieldType = fieldValue.field.type;
            const actualValue = fieldValue.value;
            
            if (filterValue === '' || filterValue === null || (Array.isArray(filterValue) && filterValue.length === 0)) {
              continue; // Пропускаем пустые фильтры
            }

            let matches = false;
            
            switch (fieldType) {
              case 'TEXT':
              case 'TEXTAREA':
                if (typeof actualValue === 'string' && typeof filterValue === 'string') {
                  matches = actualValue.toLowerCase().includes(filterValue.toLowerCase());
                }
                break;
                
              case 'DATETIME':
                if (actualValue && filterValue) {
                  const actualDate = new Date(actualValue);
                  const filterDate = new Date(filterValue);
                  // Сравниваем даты (можно настроить точность)
                  matches = actualDate.getTime() === filterDate.getTime();
                }
                break;
                
              case 'MULTISELECT':
                if (Array.isArray(actualValue) && Array.isArray(filterValue)) {
                  // Проверяем, есть ли пересечение между выбранными значениями и значениями тикета
                  matches = filterValue.some(val => actualValue.includes(val));
                }
                break;

              case 'LIST':
                if (typeof actualValue === 'string' && typeof filterValue === 'string') {
                  matches = actualValue === filterValue;
                }
                break;
                
              case 'CHECKBOX':
                if (typeof actualValue === 'boolean' && typeof filterValue === 'boolean') {
                  matches = actualValue === filterValue;
                }
                break;
            }
            
            if (!matches) {
              return false;
            }
          }
        }
        
        return true;
      });
      
      // Формируем объект с примененными фильтрами для имени файла
      const filters = {
        projectId: selectedProject,
        status: selectedStatus,
        assigneeId: selectedAssignee?.id,
        authorId: selectedAuthor?.id,
        plannedDateFrom: plannedDateFrom || null,
        plannedDateTo: plannedDateTo || null,
        customFields: customFieldsFilters
      };
      
      // Экспортируем данные в Excel
      exportTicketsToExcel(filteredTickets, projects, filters);
      
      // Убрали уведомление об успешном экспорте
    } catch (error) {
      console.error('Ошибка при экспорте тикетов в Excel:', error);
      // Также убираем уведомление об ошибке, оставляем только логирование в консоль
    } finally {
      setExportLoading(false);
    }
  };

  // Handle attachment click to download or open
  const handleAttachmentClick = (attachment) => {
    // Create URL for the attachment
    const attachmentUrl = `${process.env.REACT_APP_API_URL || ''}/api/attachments/${attachment.id}`;
    // Open in new tab
    window.open(attachmentUrl, '_blank');
  };

  const handleAddComment = async () => {
    let dataArg;
    if (commentAttachments.length) {
      const form = new FormData();
      form.append('content', newComment);
      
      // Добавляем имя файла как отдельные метаданные для сохранения кириллицы
      Array.from(commentAttachments).forEach((file, index) => {
        // Добавляем сам файл
        form.append('attachments', file);
        
        // Добавляем его имя в отдельное поле с тем же индексом
        form.append(`filename-${index}`, file.name);
        
        // Для дополнительной надежности добавляем имя файла в формате base64
        const encodedName = btoa(unescape(encodeURIComponent(file.name)));
        form.append(`filename-encoded-${index}`, encodedName);
      });
      
      // Добавляем количество файлов
      form.append('fileCount', commentAttachments.length);
      
      dataArg = form;
    } else {
      dataArg = { content: newComment };
    }
    await addComment({ ticketId: selectedTicket.id, comment: dataArg });
    setNewComment('');
    setCommentAttachments([]);
    const res = await ticketsApi.getOne(selectedTicket.id);
    setSelectedTicket(res.data);
  };
  
  // Функция редактирования комментария
  const handleEditComment = async (commentId, data) => {
    try {
      await ticketsApi.editComment(commentId, data);
      // Получаем обновленный тикет для обновления списка комментариев
      const res = await ticketsApi.getOne(selectedTicket.id);
      setSelectedTicket(res.data);
      // Обновляем список тикетов
      refetch();
      // Сбрасываем состояние редактирования
      setEditingCommentId(null);
      setEditedCommentText('');
    } catch (error) {
      console.error('Ошибка при редактировании комментария:', error);
      alert(error.response?.data?.error || 'Ошибка при редактировании комментария');
    }
  };
  
  // Начать редактирование комментария
  const startEditComment = (comment) => {
    setEditingCommentId(comment.id);
    // Сбрасываем список вложений, отмеченных для удаления
    setAttachmentsToDelete([]);
    
    // Проверяем, если комментарий в JSON формате
    if (comment.content && comment.content.startsWith('{"текст":"')) {
      try {
        const commentData = JSON.parse(comment.content);
        if (commentData.текст) {
          setEditedCommentText(commentData.текст);
          return;
        }
      } catch (e) {
        // В случае ошибки парсинга, используем оригинальный текст
      }
    }
    
    // Если комментарий не в JSON формате или произошла ошибка, используем оригинальный текст
    // Учитываем также старый префикс [System] для системных сообщений
    if (comment.content && comment.content.startsWith('[System]')) {
      setEditedCommentText(comment.content.substring(8).trim());
    } else {
      setEditedCommentText(comment.content);
    }
  };
  
  // Отменить редактирование
  const cancelEditComment = () => {
    setEditingCommentId(null);
    setEditedCommentText('');
    setAttachmentsToDelete([]); // Сбрасываем список вложений, отмеченных для удаления
  };
  
  // Сохранить отредактированный комментарий
  const saveEditComment = async () => {
    if (!editedCommentText.trim()) {
      alert('Содержание комментария не может быть пустым');
      return;
    }
    
    // Передаем и текст, и список вложений на удаление
    await handleEditComment(editingCommentId, { 
      content: editedCommentText,
      attachmentsToDelete: attachmentsToDelete
    });
  };

  const openDetailDialog = async (ticket) => {
    // Устанавливаем предварительные данные тикета
    setSelectedTicket(ticket);
    
    // Формируем URL с префиксом проекта
    const project = projects.find(p => p.id === ticket.projectId);
    const prefixedId = project?.ticketPrefix 
      ? `${project.ticketPrefix}-${ticket.id}` 
      : ticket.id;
    
    // Обновляем URL, если он отличается от текущего
    if (!ticketWithPrefix || ticketWithPrefix !== prefixedId) {
      const query = searchParams.toString();
      navigate({
        pathname: `/ticket/${prefixedId}`,
        search: query ? `?${query}` : ''
      });
    }
    
    // Открываем диалог
    setOpenDetail(true);
    
    try {
      // Загружаем актуальные статусы проекта для тикета
      if (ticket.projectId) {
        await loadProjectStatuses(ticket.projectId);
      }
      
      // Запрашиваем самые актуальные данные тикета с сервера
      const response = await ticketsApi.getOne(ticket.id);
      if (response.data) {

        setSelectedTicket(response.data);
      }
    } catch (error) {
      console.error('Ошибка при загрузке данных тикета:', error);
    }
  };
  const closeDetailDialog = () => {
    setOpenDetail(false);
    // При закрытии диалога возвращаемся на главную страницу
    const query = searchParams.toString();
    navigate({
      pathname: '/',
      search: query ? `?${query}` : ''
    });
  };

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 2 }}>
        <Box>
          <Typography variant="h4" align="center" gutterBottom>
            Система управления заявками
          </Typography>

          <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab label="Активные" />
            <Tab label="Архив" />
            <Tab label="Статистика" />
          </Tabs>
          {/* Отдельная строка для фильтра проекта (расширенная ширина) */}
          <Box sx={{ display: 'flex', mb: 2, width: '100%' }}>
            <Autocomplete
              value={projectOptions.find(p => p.id === selectedProjectId) || projectOptions[0]}
              inputValue={projectInputValue}
              onInputChange={(_, newInput) => {
                setProjectInputValue(newInput || '');
              }}
              onChange={(_, newValue) => {
                const projectId = normalizeProjectId(newValue ? newValue.id : '');
                handleProjectChange(projectId);
                if (!projectId) setProjectInputValue('');
              }}
              onOpen={() => {
                // На открытии всегда показываем весь список проектов
                setProjectInputValue('');
              }}
              options={projectOptions}
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(option, value) => normalizeProjectId(option?.id) === normalizeProjectId(value?.id)}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key} {...otherProps} style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: option.id === '' ? 'grey.400' : 'primary.main' }}>
                      {option.id === '' ? 'В' : option.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography noWrap sx={{ flex: 1, minWidth: 0 }}>
                      {option.name}
                    </Typography>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Проект"
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              )}
              noOptionsText="Нет доступных проектов"
              clearOnBlur={false}
              selectOnFocus
              handleHomeEndKeys
              sx={{ width: '100%' }}
              clearIcon={<CloseIcon />}
              disableClearable={false}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              
              
              <Autocomplete
                key={`assignee-${selectedAssignee?.id || 'null'}`}
                value={selectedAssignee}
                onChange={(event, newValue) => {

                  setSelectedAssignee(newValue);
                  setPage(0); // Сбрасываем пагинацию при изменении фильтра
                }}
                options={projectAssignees} // Используем список исполнителей
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  return (
                    <li key={key} {...otherProps} style={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: 'primary.main' }}>
                        {option.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography noWrap style={{ maxWidth: '200px' }}>
                        {option.name}
                      </Typography>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Исполнитель"
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: 200 }}
                  />
                )}
                noOptionsText="Нет доступных исполнителей"
                clearOnBlur={false}
                selectOnFocus
                handleHomeEndKeys
                sx={{ width: 200 }}
                clearIcon={<CloseIcon />}
                disableClearable={false}
              />
              
              <Autocomplete
                key={`author-${selectedAuthor?.id || 'null'}`}
                value={selectedAuthor}
                onChange={(event, newValue) => {

                  setSelectedAuthor(newValue);
                  setPage(0); // Сбрасываем пагинацию при изменении фильтра
                }}
                options={projectAuthors} // Используем список авторов
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option) => (
                  <li {...props} style={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: 'secondary.main' }}>
                      {option.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography noWrap style={{ maxWidth: '200px' }}>
                      {option.name}
                    </Typography>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Автор"
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: 200 }}
                  />
                )}
                noOptionsText="Нет доступных авторов"
                clearOnBlur={false}
                selectOnFocus
                handleHomeEndKeys
                sx={{ width: 200 }}
                clearIcon={<CloseIcon />}
                disableClearable={false}
              />
              
              {/* Фильтр по типу заявки */}
              <Autocomplete
                key={`type-${selectedType || 'null'}`}
                value={selectedType || null}
                onChange={(event, newValue) => {
                  setSelectedType(newValue || '');
                  setPage(0); // Сбрасываем пагинацию при изменении фильтра
                }}
                options={Object.keys(TYPE_LABELS)}
                getOptionLabel={(option) => TYPE_LABELS[option] || ''}
                renderInput={(params) => (
                  <TextField {...params} label="Тип" size="small" />
                )}
                sx={{ width: 180 }}
                clearIcon={<CloseIcon />}
                noOptionsText="Нет вариантов"
              />

              {/* Фильтр по статусу */}
              <Autocomplete
                key={`status-${selectedStatus?.id || 'null'}`}
                value={selectedStatus}
                onChange={(event, newValue) => {

                  setSelectedStatus(newValue);
                  setPage(0); // Сбрасываем пагинацию при изменении фильтра
                }}
                options={
                  selectedProject && projectStatuses[selectedProject] 
                    ? projectStatuses[selectedProject] 
                    : Object.keys(legacyStatusLabels).map(key => ({
                        id: key,
                        name: legacyStatusLabels[key],
                        color: STATUS_COLORS[key] || '#e0e0e0'
                      }))
                }
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option) => (
                  <li {...props} style={{ display: 'flex', alignItems: 'center' }}>
                    <Box 
                      sx={{ 
                        width: 10, 
                        height: 10, 
                        borderRadius: '50%', 
                        backgroundColor: option.color || '#e0e0e0',
                        display: 'inline-block',
                        mr: 1
                      }} 
                    />
                    <Typography noWrap style={{ maxWidth: '200px' }}>
                      {option.name}
                    </Typography>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Статус"
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: 200 }}
                  />
                )}
                noOptionsText="Нет доступных статусов"
                clearOnBlur={false}
                selectOnFocus
                handleHomeEndKeys
                sx={{ width: 200 }}
                clearIcon={<CloseIcon />}
                disableClearable={false}
              />
              
              {/* Кнопка настроек проекта удалена */}

              <TextField
                label="План. дата с"
                type="date"
                variant="outlined"
                size="small"
                value={plannedDateFrom}
                onChange={(event) => {
                  setPlannedDateFrom(event.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 170 }}
              />

              <TextField
                label="План. дата по"
                type="date"
                variant="outlined"
                size="small"
                value={plannedDateTo}
                onChange={(event) => {
                  setPlannedDateTo(event.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 170 }}
              />
            </Box>
            
            {tab !== 2 && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Экспорт в Excel">
                <span>
                  <IconButton
                    color="primary"
                    onClick={handleExportToExcel}
                    disabled={exportLoading || isLoading || tickets.length === 0}
                    size="medium"
                  >
                    {exportLoading ? <CircularProgress size={24} /> : <FileDownloadIcon />}
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                color="primary"
                sx={{ minWidth: 180 }}
                onClick={() => { setSelectedTicket(null); setIsFormOpen(true); }}
              >
                Создать заявку
              </Button>
            </Box>
            )}
          </Box>
          
          {/* Второй ряд с полем поиска */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
            <TextField
              label={t('search') || 'Поиск'}
              variant="outlined"
              size="small"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchText ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="очистить поиск"
                      onClick={() => setSearchText('')}
                      edge="end"
                      size="small"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
              placeholder="Поиск по ИД, названию или автору заявки..."
              sx={{ width: '50%' }}
            />
            
            {/* Кнопка сброса всех фильтров рядом с полем поиска */}
            {hasActiveFilters && (
              <Tooltip title="Сбросить все фильтры">
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={handleClearAllFilters}
                  startIcon={<ClearIcon />}
                  sx={{ 
                    minWidth: 'auto',
                    px: 2,
                    fontWeight: 'bold'
                  }}
                >
                  Сбросить все фильтры
                </Button>
              </Tooltip>
            )}
          </Box>

          {/* Фильтр по кастомным полям */}
          <CustomFieldsFilter
            key={`custom-fields-${Object.keys(customFieldsFilters).length}-${selectedProject}`}
            projectId={selectedProject}
            isOpen={isCustomFieldsFilterOpen}
            onToggle={() => setIsCustomFieldsFilterOpen(!isCustomFieldsFilterOpen)}
            filters={customFieldsFilters}
            onFiltersChange={handleCustomFieldsFiltersChange}
            onClearFilters={handleClearCustomFieldsFilters}
          />

          {tab !== 2 ? (
            <>
        <TableContainer component={Paper} sx={{ mb: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="none" sx={{ width: 20, minWidth: 20, maxWidth: 20 }} />
                <TableCell>{t('title') || 'Название'}</TableCell>
                <TableCell>{t('project') || 'Проект'}</TableCell>
                <TableCell sx={{ width: 130 }}>Тип</TableCell>
                <TableCell>{t('author') || 'Автор'}</TableCell>
                <TableCell>{t('status') || 'Статус'}</TableCell>
                <TableCell sx={{ width: 110 }}>{t('createdAt') || 'Создано'}</TableCell>
                <TableCell sx={{ width: 110 }}>{t('plannedEndDate') || 'План.дата'}</TableCell>

              </TableRow>
            </TableHead>
            <TableBody>
              {displayedTickets.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((ticket) => (
                <TableRow key={ticket.id} hover onClick={() => openDetailDialog(ticket)} sx={{ cursor: 'pointer', minHeight: 48 }}>
                  <TableCell padding="none" sx={{ width: 8, minWidth: 8, maxWidth: 8, background: 'transparent' }}>
                    <PriorityStripe priority={ticket.priority} />
                  </TableCell>
                  <TableCell sx={{ minWidth: 300, wordBreak: 'break-word', whiteSpace: 'normal' }}>
                    <div>
                      <span style={{ fontWeight: 500, color: '#555', marginRight: '4px' }}>
                        {projects.find(p => p.id === ticket.projectId)?.ticketPrefix ? 
                          `${projects.find(p => p.id === ticket.projectId).ticketPrefix}-${ticket.id}` : 
                          `ID-${ticket.id}`}
                      </span>
                      <span style={{ fontWeight: 400, marginLeft: '2px' }}>
                        <span style={{ color: '#777', margin: '0 6px' }}>—</span>{ticket.title}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>{projects.find(p => p.id === ticket.projectId)?.name || ticket.projectId}</TableCell>
                  <TableCell><TypeChip type={ticket.type} /></TableCell>
                  <TableCell>
                    {ticket.createdBy?.name}{ticket.createdBy?.organization && (
                      <span style={{ display: 'block', fontSize: '0.95em', opacity: 0.75 }}>
                        {ticket.createdBy.organization}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {ticket.statusObj ? (
                      <StatusChip 
                        status={ticket.statusObj.name} 
                        label={ticket.statusObj.name} 
                        color={ticket.statusObj.color} 
                      />
                    ) : (
                      <StatusChip 
                        status={ticket.status} 
                        label={legacyStatusLabels[ticket.status?.toLowerCase()] || ticket.status} 
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.85rem', lineHeight: 1.2, padding: '8px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span>{formatDatePart(ticket.createdAt, 'date')}</span>
                      <span style={{ color: '#666' }}>{formatDatePart(ticket.createdAt, 'time')}</span>
                    </div>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.85rem', padding: '8px' }}>
                    {ticket.plannedEndDate ? formatDatePart(ticket.plannedEndDate, 'date') : '-'}
                  </TableCell>

                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={displayedTickets.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t('rowsPerPage') || t('rows') || 'Строк на странице:'}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t('of')} ${count !== -1 ? count : `больше чем ${to}`}`}
        />
            </>
          ) : (
            <Paper elevation={1} sx={{ p: 3, mt: 2 }}>
              {(!selectedProject || selectedProject === '') ? (
                <Typography align="center" color="text.secondary">
                  Выберите проект, чтобы увидеть статистику.
                </Typography>
              ) : !statusStats ? (
                <Typography align="center" color="text.secondary">
                  Данные загружаются...
                </Typography>
              ) : statusStats.total === 0 ? (
                <Typography align="center" color="text.secondary">
                  В выбранном проекте пока нет тикетов.
                </Typography>
              ) : (
                <>
                  <Stack spacing={3}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        flexWrap: 'wrap',
                        gap: 2,
                      }}
                    >
                      <Typography variant="h6">
                        {statsReport === 'statuses'
                          ? 'Распределение тикетов по статусам'
                          : statsReport === 'types'
                            ? 'Распределение тикетов по типам'
                            : 'Плановые даты решения'}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                          flexWrap: 'wrap',
                          gap: 2,
                        }}
                      >
                        {statsReport === 'statuses' || statsReport === 'types' ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Typography variant="body2" color="text.secondary">
                              Всего тикетов: {statsReport === 'types' ? (typeStats?.total || 0) : statusStats.total}
                            </Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={!exportStatsLoading ? <FileDownloadIcon /> : null}
                              onClick={handleExportStatsPng}
                              disabled={exportStatsLoading}
                            >
                              {exportStatsLoading ? <CircularProgress size={18} /> : 'PNG'}
                            </Button>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: { xs: 'flex-start', sm: 'flex-end' },
                              gap: 0.5,
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Назначена плановая дата: {plannedStats?.scheduled || 0} из {plannedStats?.total || 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Без даты: {plannedStats?.withoutPlan || 0}
                            </Typography>
                          </Box>
                        )}
                        <TextField
                          select
                          label="Тип отчёта"
                          size="small"
                          value={statsReport}
                          onChange={(event) => setStatsReport(event.target.value)}
                          sx={{ minWidth: 200 }}
                        >
                          <MenuItem value="statuses">Распределение по статусам</MenuItem>
                          <MenuItem value="types">Распределение по типам</MenuItem>
                          <MenuItem value="planned">Плановые даты решения</MenuItem>
                        </TextField>
                      </Box>
                    </Box>

                    {statsReport === 'statuses' || statsReport === 'types' ? (
                      <>
                        <Box ref={statsChartRef} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {(statsReport === 'types' ? (typeStats?.items || []) : statusStats.items).map(item => {
                            const chart = statsReport === 'types' ? typeStats : statusStats;
                            const onItemClick = statsReport === 'types' ? handleTypeLinkClick : handleStatusLinkClick;
                            const maxCount = chart.max || 1;
                            const widthPercent = maxCount === 0 ? 0 : Math.round((item.count / maxCount) * 100);
                            return (
                              <Box
                                key={item.id}
                                role="button"
                                tabIndex={0}
                                onClick={(event) => onItemClick(event, item)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    onItemClick(event, item);
                                  }
                                }}
                                sx={{
                                  cursor: 'pointer',
                                  borderRadius: 1,
                                  p: 0.5,
                                  transition: 'background-color 0.2s ease',
                                  '&:hover': { backgroundColor: 'action.hover' },
                                  '&:focus-visible': {
                                    outline: '2px solid',
                                    outlineColor: 'primary.main'
                                  }
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: item.color || '#9e9e9e' }} />
                                    <Link
                                      component="button"
                                      type="button"
                                      underline="hover"
                                      onClick={(event) => onItemClick(event, item)}
                                      sx={{ color: 'inherit', fontSize: '0.875rem', fontWeight: 500, textAlign: 'left', p: 0 }}
                                    >
                                      {item.name}
                                    </Link>
                                  </Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.count}</Typography>
                                </Box>
                                <Box sx={{ width: '100%', height: 12, borderRadius: 6, backgroundColor: '#f0f0f0' }}>
                                  <Box
                                    sx={{
                                      width: `${widthPercent}%`,
                                      height: '100%',
                                      borderRadius: 6,
                                      backgroundColor: item.color || '#9e9e9e',
                                      transition: 'width 0.3s ease'
                                    }}
                                  />
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                        {statsReport === 'statuses' && statusStats.source === 'tickets' && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Статусы проекта не загружены. Отображены статусы из данных тикетов.
                          </Typography>
                        )}
                      </>
                    ) : (
                      <>
                        {plannedStats && plannedStats.items.length > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {plannedStats.items.map(item => {
                              const maxCount = plannedStats.max || 1;
                              const widthPercent = maxCount === 0 ? 0 : Math.round((item.count / maxCount) * 100);
                              const barColor = item.color || '#42a5f5';
                              const cardStyles = item.isOverdue
                                ? { backgroundColor: 'rgba(239, 83, 80, 0.08)', borderColor: 'rgba(239, 83, 80, 0.3)' }
                                : item.id === 'no-date'
                                  ? { backgroundColor: 'rgba(0, 0, 0, 0.015)', borderColor: 'rgba(0, 0, 0, 0.04)' }
                                  : { backgroundColor: 'rgba(0, 0, 0, 0.02)', borderColor: 'rgba(0, 0, 0, 0.06)' };

                              return (
                                <Box
                                  key={item.id}
                                  role="button"
                                  tabIndex={0}
                                  onClick={(event) => handlePlannedLinkClick(event, item)}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                      handlePlannedLinkClick(event, item);
                                    }
                                  }}
                                  sx={{
                                    borderRadius: 1.5,
                                    p: 1.5,
                                    border: '1px solid',
                                    borderColor: cardStyles.borderColor,
                                    backgroundColor: cardStyles.backgroundColor,
                                    transition: 'background-color 0.2s ease, border-color 0.2s ease',
                                    cursor: 'pointer',
                                    '&:hover': { backgroundColor: 'action.hover', borderColor: 'action.focus' },
                                    '&:focus-visible': {
                                      outline: '2px solid',
                                      outlineColor: 'primary.main'
                                    }
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: { xs: 'flex-start', sm: 'center' },
                                      flexWrap: 'wrap',
                                      gap: 1,
                                      mb: 1,
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: barColor }} />
                                      <Link
                                        component="button"
                                        type="button"
                                        underline="hover"
                                        onClick={(event) => handlePlannedLinkClick(event, item)}
                                        sx={{ color: 'inherit', fontSize: '0.875rem', fontWeight: 500, textAlign: 'left', p: 0 }}
                                      >
                                        {item.name}
                                      </Link>
                                      {item.relativeLabel && (
                                        <Typography variant="body2" color="text.secondary">
                                          {item.relativeLabel}
                                        </Typography>
                                      )}
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      {item.isOverdue && (
                                        <Chip label="Просрочено" size="small" color="error" variant="outlined" sx={{ fontWeight: 600 }} />
                                      )}
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.count}</Typography>
                                    </Box>
                                  </Box>
                                  <Box sx={{ width: '100%', height: 12, borderRadius: 6, backgroundColor: 'rgba(0, 0, 0, 0.08)' }}>
                                    <Box
                                      sx={{
                                        width: `${widthPercent}%`,
                                        height: '100%',
                                        borderRadius: 6,
                                        backgroundColor: barColor,
                                        transition: 'width 0.3s ease',
                                      }}
                                    />
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Данных о плановых датах пока нет.
                          </Typography>
                        )}
                        {plannedStats && plannedStats.items.some(item => item.isOverdue) && (
                          <Typography variant="caption" color="text.secondary">
                            Просроченные группы выделены красным фоном — даты уже прошли.
                          </Typography>
                        )}
                      </>
                    )}
                  </Stack>
                </>
              )}
            </Paper>
          )}

        <TicketForm
          open={isFormOpen}
          onClose={() => { setIsFormOpen(false); setSelectedTicket(null); }}
          onSubmit={selectedTicket ? handleUpdateTicket : handleCreateTicket}
          ticket={selectedTicket}
          user={user}
          projects={projects}
          projectId={selectedProject}
          authors={projectAuthors}
          assignees={projectAssignees}
        />
        <Dialog
          open={openDetail}
          onClose={closeDetailDialog}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{ pr: 5, pb: 0 }}>
            <IconButton
              aria-label="close"
              onClick={closeDetailDialog}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
                zIndex: 2
              }}
            >
              <CloseIcon />
            </IconButton>
            <Paper elevation={2} sx={{ p: 2, mt: 4, background: '#f7f7fb', borderRadius: 2 }}>
              {/* Project name */}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
                <FolderIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                {selectedTicket?.project?.name || projects.find(p => p.id === selectedTicket?.projectId)?.name || selectedTicket?.projectId || 'Не указан'}
              </Typography>
              
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                {projects.find(p => p.id === selectedTicket?.projectId)?.ticketPrefix ? `${projects.find(p => p.id === selectedTicket?.projectId).ticketPrefix}-${selectedTicket?.id}` : selectedTicket?.id}: {selectedTicket?.title}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
                  Статус:
                </Typography>
                {selectedTicket?.statusObj ? (
                  <StatusChip 
                    status={selectedTicket.statusObj.name} 
                    label={selectedTicket.statusObj.name} 
                    color={selectedTicket.statusObj.color} 
                  />
                ) : (
                  <StatusChip 
                    status={selectedTicket?.status} 
                    label={legacyStatusLabels[selectedTicket?.status?.toLowerCase()] || selectedTicket?.status} 
                  />
                )}
                
                <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>
                  Тип:
                </Typography>
                <TypeChip type={selectedTicket?.type} />

                <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>
                  Приоритет:
                </Typography>
                <PriorityChip priority={selectedTicket?.priority} label={priorityLabels[selectedTicket?.priority?.toLowerCase()] || selectedTicket?.priority} />
              </Stack>
            </Paper>
            
          </DialogTitle>
          <DialogContent dividers sx={{ overflowY: 'auto', maxHeight: '70vh', pt: 2 }}>
            {/* Блок с отладочной информацией удален */}
            {/* Информация о заявке */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                <InfoIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                Информация о заявке
              </Typography>
              
              <Grid container spacing={2}>
                {/* Автор заявки */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <PersonIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.1rem', mt: 0.5 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Автор:</Typography>
                      
                      {selectedTicket?.createdBy ? (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          <Avatar 
                            sx={{ 
                              bgcolor: 'secondary.main', 
                              width: 32, 
                              height: 32, 
                              mr: 1,
                              fontSize: '0.9rem'
                            }}
                          >
                            {selectedTicket.createdBy.name?.charAt(0) || '?'}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {selectedTicket.createdBy.name || 'Не указан'}
                            </Typography>
                            {selectedTicket.createdBy.organization && (
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                {selectedTicket.createdBy.organization}
                                {selectedTicket.createdBy.email && `, ${selectedTicket.createdBy.email}`}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="body2">
                          Не указан
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                    <EventIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.1rem' }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>План. дата:</Typography>
                    <Typography variant="body2">
                      {selectedTicket?.plannedEndDate ? new Date(selectedTicket.plannedEndDate).toLocaleDateString() : 'Не указана'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.1rem' }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Создана:</Typography>
                    <Typography variant="body2">
                      {selectedTicket?.createdAt ? new Date(selectedTicket.createdAt).toLocaleDateString() : 'Не указана'}
                    </Typography>
                  </Box>
                </Grid>
                
                {/* Ответственный за заявку */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <SupportAgentIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.1rem', mt: 0.5 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Ответственный:</Typography>
                      
                      {selectedTicket?.assignedTo ? (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          <Avatar 
                            sx={{ 
                              bgcolor: 'primary.main', 
                              width: 32, 
                              height: 32, 
                              mr: 1,
                              fontSize: '0.9rem'
                            }}
                          >
                            {selectedTicket.assignedTo.name.charAt(0)}
                          </Avatar>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {selectedTicket.assignedTo.name}
                              </Typography>
                              <Chip 
                                icon={<SupportAgentIcon sx={{ fontSize: '0.7rem !important' }} />} 
                                label="Агент" 
                                size="small" 
                                color="primary" 
                                variant="outlined" 
                                sx={{ ml: 1, height: 20, '& .MuiChip-label': { fontSize: '0.7rem', px: 0.5 } }} 
                              />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                              {selectedTicket.assignedTo.organization && `${selectedTicket.assignedTo.organization}, `}
                              {selectedTicket.assignedTo.email}
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="body2">
                          Не назначен
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {selectedTicket?.customFieldValues?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Дополнительные поля
                  </Typography>
                  <Box>
                    {selectedTicket.customFieldValues.map((cf, idx) => {
                      const label = cf.field?.label || cf.field?.name || 'Поле';
                      const type = cf.field?.type;
                      let val = cf.value;
                      if (type === 'MULTISELECT' && Array.isArray(val)) {
                        val = val.join(', ');
                      } else if (type === 'CHECKBOX') {
                        val = val ? 'Да' : 'Нет';
                      } else if (type === 'DATETIME' && val) {
                        try { val = new Date(val).toLocaleString('ru-RU'); } catch (_) {}
                      }
                      return (
                        <Box
                          key={cf.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            py: 0.75,
                            px: 1,
                            borderRadius: 1,
                            backgroundColor: idx % 2 === 1 ? 'grey.50' : 'transparent'
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ minWidth: { xs: 140, sm: 200, md: 240 }, pr: 2 }}
                          >
                            {label}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, whiteSpace: 'pre-wrap', wordBreak: 'break-word', flex: 1 }}
                          >
                            {val ?? '—'}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Paper>
            
            {/* Описание */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                <DescriptionIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                Описание
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }} component="div">
                {parseLinks(selectedTicket?.description, projects) || 'Описание отсутствует'}
              </Typography>
            </Paper>
            {/* Вложения: показываем только не-аудио */}
            {selectedTicket?.attachments?.some(a => !isAudioFile(a.filename || a.originalName)) && (
              <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                  <AttachFileIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                  Вложения
                </Typography>
                <Grid container spacing={1}>
                  {selectedTicket.attachments.filter(a => !isAudioFile(a.filename || a.originalName)).map((attachment) => (
                    <Grid item key={attachment.id}>
                      <FileAttachment attachment={attachment} />
                    </Grid>
                  ))}
                </Grid>

              {selectedTicket?.customFieldValues?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Дополнительные поля
                  </Typography>
                  <Grid container spacing={1}>
                    {selectedTicket.customFieldValues.map(cf => {
                      const label = cf.field?.label || cf.field?.name || 'Поле';
                      const type = cf.field?.type;
                      let val = cf.value;
                      if (type === 'MULTISELECT' && Array.isArray(val)) {
                        val = val.join(', ');
                      } else if (type === 'CHECKBOX') {
                        val = val ? 'Да' : 'Нет';
                      } else if (type === 'DATETIME' && val) {
                        try { val = new Date(val).toLocaleString('ru-RU'); } catch (_) {}
                      }
                      return (
                        <React.Fragment key={cf.id}>
                          <Grid item xs={5} sm={4} md={3}>
                            <Typography variant="body2" color="text.secondary">{label}</Typography>
                          </Grid>
                          <Grid item xs={7} sm={8} md={9}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{val ?? '—'}</Typography>
                          </Grid>
                        </React.Fragment>
                      );
                    })}
                  </Grid>
                </Box>
              )}
              </Paper>
            )}
            {/* Аудио-вложения из описания */}
            {selectedTicket?.attachments?.some(a => isAudioFile(a.filename || a.originalName)) && (
              <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Аудио
                </Typography>
                <Stack direction="column" spacing={1}>
                  {selectedTicket.attachments.filter(a => isAudioFile(a.filename || a.originalName)).map(att => (
                    <FileAttachment key={att.id} attachment={att} />
                  ))}
                </Stack>
              </Paper>
            )}
            {/* История заявки */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                <ChatIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                {"История заявки"}
              </Typography>
              <List sx={{ p: 0, mb: 2 }}>
                {selectedTicket?.comments?.length > 0 ? selectedTicket.comments.map(comment => {
                // Проверяем, является ли комментарий системным по полю system
                // или по старому способу через префикс [System] в тексте
                const hasSystemPrefix = comment.content && comment.content.startsWith('[System]');
                const isSystemComment = comment.system === true || hasSystemPrefix;
                
                // Проверяем, является ли комментарий отредактированным (JSON формат)
                let displayContent = comment.content;
                let editInfo = null;
                
                // Проверяем, если комментарий в JSON формате
                if (comment.content && comment.content.startsWith('{"текст":"')) {
                  try {
                    const commentData = JSON.parse(comment.content);
                    if (commentData.текст && commentData.редактирован) {
                      displayContent = commentData.текст;
                      editInfo = commentData.редактирован;
                    }
                  } catch (e) {
                    // В случае ошибки парсинга оставляем оригинальный текст
                  }
                } else if (hasSystemPrefix) {
                  displayContent = comment.content.substring(8); // Удаляем префикс [System]
                }
                
                const authorName = (comment.user && comment.user.name)
                  || (comment.author && typeof comment.author === 'object' && comment.author.name)
                  || (comment.createdBy && typeof comment.createdBy === 'object' && comment.createdBy.name)
                  || (typeof comment.author === 'string' ? comment.author : '')
                  || t('unknown') || 'Unknown';
                const authorOrg = (comment.user && comment.user.organization)
                  || (comment.author && typeof comment.author === 'object' && comment.author.organization)
                  || (comment.createdBy && typeof comment.createdBy === 'object' && comment.createdBy.organization)
                  || '';
                
                // Для системных комментариев используем другой стиль
                if (isSystemComment) {
                  return (
                    <Box key={comment.id} sx={{ 
                      background: '#f0f5f9', 
                      borderRadius: 2, 
                      p: '6px 16px',
                      mb: 1,
                      border: '1px dashed #ccd7e0',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <InfoIcon sx={{ 
                        fontSize: '0.9rem', 
                        color: '#5a8db8', 
                        mr: 1 
                      }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ 
                          color: '#2c5282', 
                          fontWeight: 500,
                          fontSize: '0.85em',
                          '& span': { fontStyle: 'italic' }
                        }}>
                          {displayContent}
                          <span style={{ fontSize: '0.9em', color: '#64748b', marginLeft: '8px', display: 'inline-flex', alignItems: 'center' }}>(
                            <AccessTimeIcon sx={{ fontSize: '0.8rem', mr: 0.5, opacity: 0.7 }} />
                            {`${new Date(comment.createdAt).toLocaleDateString('ru-RU', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })} ${new Date(comment.createdAt).toLocaleTimeString('ru-RU', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}`}
                          )</span>
                        </Typography>
                      </Box>
                    </Box>
                  );
                }
                
                // Для обычных комментариев используем стиль, похожий на системные, но с другой иконкой
                return (
                  <Box key={comment.id} sx={{ 
                    background: '#f9f6f0', 
                    borderRadius: 2, 
                    p: '6px 16px',
                    mb: 1,
                    border: '1px dashed #e0d5c0',
                    display: 'flex',
                    alignItems: 'flex-start'
                  }}>
                    <ChatBubbleOutlineIcon sx={{ 
                      fontSize: '0.9rem', 
                      color: '#b8915a', 
                      mr: 1,
                      mt: 0.5
                    }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ 
                        color: '#2c5282', 
                        fontWeight: 500,
                        fontSize: '0.85em',
                        mb: 0.5
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Box>
                            <Box component="span" sx={{ fontWeight: 'bold', color: '#333', display: 'block' }}>{authorName}</Box>
                            {authorOrg && (
                              <Box component="span" sx={{ color: '#666', opacity: 0.85, fontSize: '0.9em', display: 'block' }}>{authorOrg}</Box>
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ mt: 1 }}>
                          {editingCommentId === comment.id ? (
                            <Box sx={{ mt: 1 }}>
                              <TextField
                                multiline
                                fullWidth
                                minRows={2}
                                maxRows={6}
                                variant="outlined"
                                value={editedCommentText}
                                onChange={(e) => setEditedCommentText(e.target.value)}
                                size="small"
                              />
                              
                              {/* Список вложений с возможностью удаления (показываем все типы в редакторе) */}
                              {comment.attachments?.length > 0 && (
                                <Box sx={{ mt: 1, mb: 1 }}>
                                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                                    Прикрепленные файлы:
                                  </Typography>
                                  <Stack direction="row" spacing={1} flexWrap="wrap">
                                    {comment.attachments.map(attachment => (
                                      <Chip
                                        key={attachment.id}
                                        label={attachment.filename}
                                        variant={attachmentsToDelete.includes(attachment.id) ? "outlined" : "filled"}
                                        color={attachmentsToDelete.includes(attachment.id) ? "error" : "default"}
                                        onDelete={() => {
                                          // Если файл уже отмечен для удаления, убираем отметку, иначе добавляем
                                          if (attachmentsToDelete.includes(attachment.id)) {
                                            setAttachmentsToDelete(attachmentsToDelete.filter(id => id !== attachment.id));
                                          } else {
                                            setAttachmentsToDelete([...attachmentsToDelete, attachment.id]);
                                          }
                                        }}
                                        deleteIcon={
                                          attachmentsToDelete.includes(attachment.id) ? 
                                            <UndoIcon fontSize="small" /> : 
                                            <DeleteIcon fontSize="small" />
                                        }
                                      />
                                    ))}
                                  </Stack>
                                  {attachmentsToDelete.length > 0 && (
                                    <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, display: 'block' }}>
                                      Отмечено к удалению: {attachmentsToDelete.length} файл(ов)
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                <Button onClick={cancelEditComment} sx={{ mr: 1 }} size="small">
                                  Отменить
                                </Button>
                                <Button 
                                  onClick={saveEditComment} 
                                  variant="contained" 
                                  color="primary" 
                                  size="small"
                                  disabled={!editedCommentText.trim()}
                                >
                                  Сохранить
                                </Button>
                              </Box>
                            </Box>
                          ) : (
                            <>
                              {parseLinks(displayContent, projects)}
                              {editInfo && (
                                <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary', fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                                  Отредактировано: {new Date(editInfo.время).toLocaleString('ru-RU', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </Typography>
                              )}
                            </>
                          )}
                        </Box>
                        <Box sx={{ fontSize: '0.9em', color: '#64748b', mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <AccessTimeIcon sx={{ fontSize: '0.9rem', mr: 0.5, opacity: 0.7 }} />
                            {`${new Date(comment.createdAt).toLocaleDateString('ru-RU', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })} ${new Date(comment.createdAt).toLocaleTimeString('ru-RU', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}`}
                          </Box>
                          
                          {/* Кнопка редактирования для комментариев текущего пользователя */}
                          {user && 
                           comment.userId === user.id && 
                           !isSystemComment && 
                           !selectedTicket?.statusObj?.isFinal && 
                           editingCommentId !== comment.id && (
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                startEditComment(comment);
                              }} 
                              sx={{ ml: 1, p: 0.5 }}
                            >
                              <EditIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          )}
                        </Box>
                      </Typography>
                      
                      {/* Сначала аудио */}
                      {comment.attachments?.some(a => isAudioFile(a.filename || a.originalName)) && (
                        <Stack direction="column" spacing={1} sx={{ mt: 1 }}>
                          {comment.attachments.filter(a => isAudioFile(a.filename || a.originalName)).map(attachment => (
                            <FileAttachment key={attachment.id} attachment={attachment} />
                          ))}
                        </Stack>
                      )}
                      {/* Затем прочие файлы */}
                      {comment.attachments?.some(a => !isAudioFile(a.filename || a.originalName)) && (
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                          {comment.attachments.filter(a => !isAudioFile(a.filename || a.originalName)).map(attachment => (
                            <FileAttachment key={attachment.id} attachment={attachment} />
                          ))}
                        </Stack>
                      )}
                    </Box>
                  </Box>
                );
              }) : (
                <ListItem>
                  <ListItemText primary={t('noComments') || 'Нет событий'} />
                </ListItem>
              )}
            </List>
            </Paper>
            {/* Add comment form with attachments */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
              <form onSubmit={e => {
                e.preventDefault();
                handleAddComment();
              }}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  value={newComment}
                  onChange={e => setNewComment(e.target.value)}
                  placeholder={t('addComment') + '...'}
                  sx={{ mb: 1 }}
                />
                <input
                  accept="*"
                  style={{ display: 'none' }}
                  id="comment-attachments"
                  multiple
                  type="file"
                  onChange={e => {
                    const files = Array.from(e.target.files || []);
                    if (files.length === 0) return;
                    setCommentAttachments(prev => {
                      const combined = [...(prev || []), ...files];
                      const seen = new Set();
                      const unique = [];
                      for (const f of combined) {
                        const key = `${f.name}|${f.size}|${f.lastModified}`;
                        if (!seen.has(key)) {
                          seen.add(key);
                          unique.push(f);
                        }
                      }
                      return unique;
                    });
                    // allow selecting same files again
                    e.target.value = null;
                  }}
                />
                <label htmlFor="comment-attachments">
                  <IconButton color="primary" component="span" title={t('attachFile')}>
                    <AttachFileIcon />
                  </IconButton>
                </label>
                {commentAttachments && commentAttachments.length > 0 && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1, mb: 1 }}>
                    {commentAttachments.map((file, idx) => (
                      <Chip 
                        key={`${file.name}|${file.size}|${file.lastModified}|${idx}`} 
                        label={file.name}
                        onDelete={() => {
                          setCommentAttachments(prev => prev.filter((_, i) => i !== idx));
                        }}
                      />
                    ))}
                  </Stack>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ mt: 1 }}
                  disabled={!newComment.trim() && (!commentAttachments || commentAttachments.length === 0)}
                >
                  {t('addComment')}
                </Button>
              </form>
            </Paper>

          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {/* Левые кнопки управления заявкой */}
              {/* Кнопка "Взять тикет" для агентов */}
              {user?.role === 'agent' && !selectedTicket?.statusObj?.isFinal && 
               (!selectedTicket?.assignedToId || String(selectedTicket?.assignedToId) !== String(user?.id)) && (
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => {
                    // Взять тикет
                    
                    // Отправляем запрос с пустым assignedToId, чтобы бэкенд использовал текущий userId из токена
                    ticketsApi.update(selectedTicket.id, {
                      _action: "takeTicket" // Отправляем специальное действие, чтобы бэкенд понял, что мы хотим назначить текущего пользователя
                    }).then((response) => {

                      
                      // Перезагружаем тикет для получения обновленных данных
                      ticketsApi.getOne(selectedTicket.id).then(res => {
                        setSelectedTicket(res.data);
                        // Обновляем список тикетов
                        refetch();
                      });
                    }).catch(err => {
                      console.error('Ошибка при назначении тикета:', err);
                      console.error('Текст ошибки:', err.response?.data?.error);
                      
                      // Если первый способ не сработал, попробуем альтернативный подход

                      
                      // Попробуем получить актуальные данные текущего пользователя
                      authApi.me().then(meResponse => {

                        
                        // Пробуем отправить запрос с userId из ответа me
                        ticketsApi.update(selectedTicket.id, { 
                          assignedToId: meResponse.data.userId || meResponse.data.id
                        }).then((response) => {

                          ticketsApi.getOne(selectedTicket.id).then(res => {
                            setSelectedTicket(res.data);
                            refetch();
                          });
                        }).catch(secondErr => {
                          console.error('Ошибка второй попытки:', secondErr);
                        });
                      });
                    });
                  }}
                  startIcon={<SupportAgentIcon />}
                >
                  Взять тикет
                </Button>
              )}
               {/* Кнопка закрытия для автора тикета */}
               {user && selectedTicket && !selectedTicket?.statusObj?.isFinal && canAuthorActFromCurrentStatus &&
                (String(selectedTicket?.createdById) === String(user.id) || String(selectedTicket?.createdBy?.id) === String(user.id)) && (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={async () => {
                      try {
                        const projectId = selectedTicket.projectId || selectedTicket.project?.id;
                        const statuses = projectStatuses[projectId] || [];
                        const projectCfg = projects.find(p => String(p.id) === String(projectId));
                        // Если задан источник допустимых действий, проверяем текущее соответствие
                        const allowFromId = projectCfg?.authorActionsFromStatusId;
                        if (allowFromId && (!selectedTicket.statusObj || String(selectedTicket.statusObj.id) !== String(allowFromId))) {
                          alert('Закрытие автором запрещено из текущего статуса.');
                          return;
                        }
                        // 1) Явно заданный статус закрытия автором
                        let finalStatus = projectCfg?.authorCloseStatusId ? statuses.find(s => String(s.id) === String(projectCfg.authorCloseStatusId)) : null;
                        // 2) Если не задан — используем эвристики/финальные
                        if (!finalStatus) {
                          const normalize = (v) => String(v || '').trim().toLowerCase();
                          const exactClosed = statuses.find(s => {
                            const n = normalize(s.name);
                            return n === 'закрыто' || n === 'закрыт' || n === 'closed' || n === 'close';
                          });
                          const containsClosed = statuses.find(s => normalize(s.name).includes('закры'));
                          const exactResolved = statuses.find(s => {
                            const n = normalize(s.name);
                            return n === 'решен' || n === 'решено' || n === 'resolved' || n === 'done';
                          });
                          const finalByMaxOrder = statuses
                            .filter(s => s.isFinal)
                            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                            .slice(-1)[0];
                          const anyFinal = statuses.find(s => s.isFinal);
                          finalStatus = exactClosed || containsClosed || exactResolved || finalByMaxOrder || anyFinal || null;
                        }
                        if (!finalStatus) {
                          alert('Не найден финальный статус для проекта. Обратитесь к администратору.');
                          return;
                        }
                        await updateTicket({
                          id: selectedTicket.id,
                          ticket: {
                            statusId: finalStatus.id,
                            priority: selectedTicket.priority,
                            projectId: projectId
                          }
                        });
                        const res = await ticketsApi.getOne(selectedTicket.id);
                        setSelectedTicket(res.data);
                        refetch();
                      } catch (e) {
                        console.error('Ошибка при закрытии заявки автором:', e);
                        alert(e.response?.data?.error || 'Не удалось закрыть заявку');
                      }
                    }}
                    startIcon={<CloseIcon />}
                  >
                    Закрыть заявку
                  </Button>
               )}
               {/* Кнопка "Вернуть на доработку" для автора тикета */}
               {user && selectedTicket && !selectedTicket?.statusObj?.isFinal && canAuthorActFromCurrentStatus &&
                (String(selectedTicket?.createdById) === String(user.id) || String(selectedTicket?.createdBy?.id) === String(user.id)) && (
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={async () => {
                      try {
                        const projectId = selectedTicket.projectId || selectedTicket.project?.id;
                        const statuses = projectStatuses[projectId] || [];
                        if (!Array.isArray(statuses) || statuses.length === 0) {
                          alert('Не удалось определить статусы проекта для возврата на доработку.');
                          return;
                        }
                        const projectCfg = projects.find(p => String(p.id) === String(projectId));
                        // Если задан источник допустимых действий, проверяем текущее соответствие
                        const allowFromId = projectCfg?.authorActionsFromStatusId;
                        if (allowFromId && (!selectedTicket.statusObj || String(selectedTicket.statusObj.id) !== String(allowFromId))) {
                          alert('Возврат на доработку запрещен из текущего статуса.');
                          return;
                        }
                        // 1) Явно заданный статус возврата на доработку
                        let targetStatus = projectCfg?.reworkStatusId ? statuses.find(s => String(s.id) === String(projectCfg.reworkStatusId)) : null;
                        if (!targetStatus) {
                          const normalize = (v) => String(v || '').trim().toLowerCase();
                          const byInclude = (substr) => statuses.find(s => normalize(s.name).includes(substr));
                          // Эвристики
                          const sRework = byInclude('доработ');
                          const sReopenRu = byInclude('переоткры');
                          const sReopenEn = byInclude('reopen') || byInclude('re-open');
                          const sOpenRu = byInclude('открыт');
                          const sOpenEn = byInclude('open');
                          const sNewRu = byInclude('нов');
                          const sInitial = statuses.find(s => s.isInitial);
                          const firstNonFinal = statuses
                            .filter(s => !s.isFinal)
                            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))[0];
                          targetStatus = sRework || sReopenRu || sReopenEn || sOpenRu || sOpenEn || sNewRu || sInitial || firstNonFinal || null;
                        }

                        if (!targetStatus) {
                          alert('Не найден подходящий статус для возврата на доработку. Обратитесь к администратору.');
                          return;
                        }

                        // Если статус не меняется, просто выходим
                        if (selectedTicket.statusObj && targetStatus.id === selectedTicket.statusObj.id) {
                          alert('Тикет уже находится в статусе для доработки.');
                          return;
                        }

                        await updateTicket({
                          id: selectedTicket.id,
                          ticket: {
                            statusId: targetStatus.id,
                            priority: selectedTicket.priority,
                            projectId: projectId
                          }
                        });
                        const res = await ticketsApi.getOne(selectedTicket.id);
                        setSelectedTicket(res.data);
                        refetch();
                      } catch (e) {
                        console.error('Ошибка при возврате заявки на доработку автором:', e);
                        alert(e.response?.data?.error || 'Не удалось вернуть заявку на доработку');
                      }
                    }}
                    startIcon={<UndoIcon />}
                    sx={{ ml: 1 }}
                  >
                    Вернуть на доработку
                  </Button>
               )}
              {/* Показываем кнопку редактирования только если:
                 1) тикет не в финальном статусе И
                 2) пользователь админ ИЛИ (пользователь агент И ему назначен этот тикет) */}
              {!selectedTicket?.statusObj?.isFinal && ((user?.role === 'admin') || (user?.role === 'agent' && String(selectedTicket?.assignedToId) === String(user?.id))) && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      handleEditTicket(selectedTicket);
                      closeDetailDialog();
                    }}
                    startIcon={<EditIcon />}
                    sx={{ ml: user?.role === 'agent' ? 1 : 0 }}
                  >
                    {t('edit')}
                  </Button>
                )
              }
              {/* Кнопка удаления видна только администраторам */}
              {user?.role === 'admin' && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    setTicketToDelete(selectedTicket);
                    setConfirmDeleteOpen(true);
                  }}
                  startIcon={<DeleteIcon />}
                  sx={{ ml: 1 }}
                >
                  {t('delete')}
                </Button>
              )}
              
              {/* Смена статуса через выпадающий список для админа или назначенного агента */}
              {((user?.role === 'admin') || (user?.role === 'agent' && String(selectedTicket?.assignedToId) === String(user?.id))) && selectedTicket && (
                <Tooltip 
                  title={selectedTicket.statusObj?.isFinal ? "Финальный статус - изменение недоступно" : ""}
                  placement="top"
                  arrow
                >
                  <FormControl variant="outlined" size="small" sx={{ ml: 1, minWidth: 160 }}>
                    <InputLabel id="change-status-label">{t('status')}</InputLabel>
                    <Select
                    labelId="change-status-label"
                    value={selectedTicket.statusObj?.id || ''}
                    onChange={(e) => {
                      const newStatusId = e.target.value;
                      const projectId = selectedTicket.projectId || selectedTicket.project?.id;
                      
                      if (projectId && projectStatuses[projectId]) {
                        const selectedStatus = projectStatuses[projectId].find(s => s.id === newStatusId);
                        
                        if (selectedStatus && (!selectedTicket.statusObj || newStatusId !== selectedTicket.statusObj.id)) {
                          // Обновляем тикет с новым статусом
                          updateTicket({ 
                            id: selectedTicket.id, 
                            ticket: { 
                              statusId: newStatusId,
                              priority: selectedTicket.priority, // Сохраняем текущий приоритет
                              projectId: projectId
                            } 
                          }).then(() => {
                            // Перезагружаем тикет для получения обновленных данных
                            ticketsApi.getOne(selectedTicket.id).then(res => setSelectedTicket(res.data));
                            // Инвалидируем кэш тикетов для обновления списка
                            refetch();
                          });
                        }
                      }
                    }}
                    label={t('status')}
                    disabled={selectedTicket.statusObj?.isFinal}
                  >
                    {selectedTicket.projectId && projectStatuses[selectedTicket.projectId] ? (
                      projectStatuses[selectedTicket.projectId].map(status => (
                        <MenuItem 
                          key={status.id} 
                          value={status.id}
                          disabled={selectedTicket.statusObj?.id === status.id}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box 
                              component="span" 
                              sx={{ 
                                width: 10, 
                                height: 10, 
                                borderRadius: '50%', 
                                backgroundColor: status.color,
                                display: 'inline-block',
                                mr: 0.5
                              }} 
                            />
                            {status.name}
                          </Box>
                        </MenuItem>
                      ))
                    ) : (
                      // Запасной вариант со старыми статусами
                      <>
                        <MenuItem value="new" disabled={selectedTicket.status === 'new'}>
                          {legacyStatusLabels.new || t('new')}
                        </MenuItem>
                        <MenuItem value="in_progress" disabled={selectedTicket.status === 'in_progress'}>
                          {legacyStatusLabels.in_progress || t('inProgress')}
                        </MenuItem>
                        <MenuItem value="resolved" disabled={selectedTicket.status === 'resolved'}>
                          {legacyStatusLabels.resolved || t('resolved')}
                        </MenuItem>
                        <MenuItem value="closed" disabled={selectedTicket.status === 'closed'}>
                          {legacyStatusLabels.closed || t('closed')}
                        </MenuItem>
                      </>
                    )}
                  </Select>
                </FormControl>
              </Tooltip>
              )}
            </Box>
            
            {/* Правая кнопка закрытия */}
            <Button 
              variant="contained" 
              color="primary"
              onClick={closeDetailDialog}
            >
              {t('close')}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Диалог подтверждения удаления тикета */}
        <Dialog
          open={confirmDeleteOpen}
          onClose={() => setConfirmDeleteOpen(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {t('deleteTicket') || 'Удалить заявку?'}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" id="alert-dialog-description">
              {t('confirmDeleteTicket') || 'Вы действительно хотите удалить эту заявку? Это действие нельзя отменить.'}
            </Typography>
            {ticketToDelete && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {projects.find(p => p.id === ticketToDelete.projectId)?.ticketPrefix ? 
                    `${projects.find(p => p.id === ticketToDelete.projectId).ticketPrefix}-${ticketToDelete.id}` : 
                    `ID-${ticketToDelete.id}`}
                  <span style={{ color: '#777', margin: '0 6px' }}>—</span>
                  {ticketToDelete.title}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDeleteOpen(false)}>{t('cancel')}</Button>
            <Button 
              onClick={() => {
                if (ticketToDelete) {
                  handleDeleteTicket(ticketToDelete.id);
                  setConfirmDeleteOpen(false);
                  closeDetailDialog();
                }
              }} 
              color="error" 
              variant="contained"
              autoFocus
            >
              {t('delete')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
    </>
  );
}

export default TicketsPage;
